import {
  AbsoluteCenter,
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  ChakraProvider,
  Collapse,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  IconButton,
  Radio,
  RadioGroup,
  Select,
  SimpleGrid,
  Spinner,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Tooltip,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon, EditIcon, InfoOutlineIcon, SettingsIcon, StarIcon } from '@chakra-ui/icons';
import { createColumnHelper } from '@tanstack/react-table';
import {
  TEARSHEET_VERSION,
  deleteTearsheetApi,
  fetchTearsheetNewApi,
  getTearsheetApi,
  getTearsheetCashflowScenarioApi,
  getTearsheetListApi,
  getTearsheetAccountingCashBalanceApi,
  saveTearsheetNewApi,
} from 'api/tearsheetApi';
import ResultsTable from '../../../CashflowModel/ResultsTable';
import { normalizeStoredCashflowResult } from '../../../CashflowModel/normalizeStoredCashflowResult';
import CohortBreakdownCsv from '../../../CashflowModel/CohortBreakdownCsv';
import RepaymentsPerCohortCsv from '../../../CashflowModel/RepaymentsPerCohortCsv';
import { buildCohortRevenueBreakdownCsvString } from '../../../CashflowModel/buildCohortRevenueBreakdownCsv';
import { buildRepaymentsPerCohortCsvString } from '../../../CashflowModel/buildRepaymentsPerCohortCsv';
import DataTable from 'components/DataTable';
import Card from 'components/card/Card';
import SelectInput from 'components/inputs/SelectInput';
import TwMenu from 'components/menu/Menu';
import { CockpitUsersContext } from 'context/CockpitUsersContext';
import JSZip from 'jszip';
import moment from 'moment';
import Papa from 'papaparse';
import { createRef, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { saveAs } from 'file-saver';
import { tearsheetAssistApi } from 'api/cockpitAssistApi';
import CockpitAssistFab from 'components/CockpitAssistFab';
import { buildTearsheetAssistContext } from './buildTearsheetAssistContext';
import { createRoot } from 'react-dom/client';
import generatePDF, { Margin } from 'react-to-pdf';
import themeV1 from 'themes/v1/theme';
import themeV2 from 'themes/v2/theme';
import { MemoryRouter, useNavigate, useParams } from 'react-router-dom';

import { buildRoasCohortCsvString } from './buildRoasCohortCsv';
import { buildRunoffRunwayDebugCsvString } from './buildRunoffRunwayDebugCsv';
import {
  computeRoasTargetCovenantSummary,
  formatRoasCovenantBasisStatus,
  roasCovenantBasisTextColor,
  roasCovenantOverallAlertStatus,
} from './roasTargetCovenantSummary';
import { buildRoasTargetValueByCohortPeriod } from './roasTargetPeriods';
import { resolveRoasGrossNetViewPolicy } from './roasGrossNetViewPolicy';
import {
  buildCurrentExposureTooltip,
  buildMaxModeledExposureTooltip,
  buildPostFundingExposureTooltip,
  displayTearsheetCovenantKpi,
  isPostFundingExposureKpi,
  orderTearsheetExposureResultRows,
  TEARSHEET_KPI_CURRENT_EXPOSURE,
  TEARSHEET_KPI_MAX_MODELED_EXPOSURE,
} from './tearsheetCovenantExposure';
import TearsheetCashflowCsvHarness from './TearsheetCashflowCsvHarness';
import TearsheetNewPdfDocument from './TearsheetNewPdfDocument';
import { buildCashflowCsvPrefillFromScenarioRequest } from './tearsheetScenarioPrefillForCsv';

/** Cashflow KPI rows hidden from the Metrics table (still supported in older saved payloads). */
const HIDDEN_CASHFLOW_METRIC_LABELS = new Set([
  'Max Exposure',
  '% Exposure To Cash',
  '% Exposure To Run Rate',
  '% Exposure To Runoff',
]);

/**
 * Annualized run rate: average of Total Revenue at MOB -1, -2, -3 (cashflow model), × 12.
 * Aligns column order with ResultsTable (sorted by period).
 */
const computeAnnualRunrateFromCashflowItems = (items) => {
  const pl = items?.profit_and_loss ?? items?.profitAndLoss;
  const totalRevenue = pl?.total_revenue ?? pl?.totalRevenue;
  const cogs = pl?.cogs;
  if (!pl || !Array.isArray(cogs) || !Array.isArray(totalRevenue)) {
    return null;
  }
  if (cogs.length !== totalRevenue.length || cogs.length === 0) {
    return null;
  }
  const rows = cogs.map((c, i) => {
    const raw = c?.date;
    if (!raw) {
      return null;
    }
    const momentDate = moment({
      year: raw.year,
      month: raw.month - 1,
      day: raw.day,
    });
    const revCell = totalRevenue[i];
    const amount =
      revCell?.amount != null ? revCell.amount : revCell?.Amount != null ? revCell.Amount : null;
    return { momentDate, amount };
  }).filter(Boolean);
  rows.sort((a, b) => a.momentDate.diff(b.momentDate));
  const mToday = moment({
    year: moment().year(),
    month: moment().month(),
    day: 1,
  });
  const mobVals = [];
  for (const z of rows) {
    const monthDiff = z.momentDate.diff(mToday, 'months');
    if (monthDiff === -1 || monthDiff === -2 || monthDiff === -3) {
      if (typeof z.amount === 'number' && Number.isFinite(z.amount)) {
        mobVals.push(z.amount);
      }
    }
  }
  if (mobVals.length === 0) {
    return null;
  }
  const avg = mobVals.reduce((s, v) => s + v, 0) / mobVals.length;
  return avg * 12;
};

/** @param {unknown} value */
const finiteNumberOrNull = (value) =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const buildTearsheetNewZipFilename = (payload, createdAt) => {
  const datePart = createdAt
    ? moment(createdAt).format('YYYY_MM_DD')
    : moment().format('YYYY_MM_DD');
  const rawName = String(payload?.name || 'tearsheet').trim() || 'tearsheet';
  const namePart = rawName
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .slice(0, 80);
  return `${namePart || 'tearsheet'}_${datePart}.zip`;
};

/**
 * Merges session scorecard overrides into rows for the save payload.
 * Rows without an entry in `overrides` are left unchanged (e.g. persisted snapshot on reload).
 * @param {Array<Record<string, unknown>>} rows
 * @param {Record<string, { score: number; reason: string; numericValue: number | null }>} overrides
 */
const mergeCashflowScorecardForSave = (rows, overrides) => {
  if (!Array.isArray(rows)) {
    return [];
  }
  return rows.map((row) => {
    const key = row.key;
    if (key == null) {
      return row;
    }
    const o = overrides[key];
    if (o === undefined) {
      return row;
    }
    const s = Number(o.score);
    if (!Number.isInteger(s) || s < 1 || s > 3) {
      return row;
    }
    const num =
      o.numericValue != null &&
      typeof o.numericValue === 'number' &&
      Number.isFinite(o.numericValue)
        ? o.numericValue
        : null;
    return {
      ...row,
      score: s,
      overrideActive: true,
      overrideReason: o.reason != null ? String(o.reason) : '',
      overrideNumericValue: num != null ? num : undefined,
    };
  });
};

const EXPOSURE_TO_RUN_RATE_TOOLTIP =
  'Run rate is annualized revenue: the average of Total Revenue from the cashflow model at MOB −1, MOB −2, and MOB −3, multiplied by 12. Actual is Post-Funding Exposure divided by this run rate, expressed as a percentage.';

/** Shown in Cashflow model → Metrics, between Runway Revenue and Duration. */
const CASHFLOW_METRIC_ANNUAL_RUN_RATE_LABEL = 'Annual Run Rate';

const CASHFLOW_METRIC_ANNUAL_RUN_RATE_TOOLTIP =
  'Annual Run Rate is the average of Total Revenue from the cashflow model at MOB −1, MOB −2, and MOB −3 (sorted by period), multiplied by 12. Same value as used for Exposure to Run Rate in the Final Score summary.';

const SCORECARD_ROW_NAMES_MONTHS = new Set([
  'Runoff (With Cash)',
  'Runoff (Without Cash)',
  'Runway',
  'Cash Balance',
  'CAC Payback',
]);

const SCORECARD_ROW_REVENUE_GROWTH = 'Revenue Growth (YoY)';

const SCORECARD_ROW_NAMES_STRING_VALUE = new Set([
  'ROAS Vector',
  'Investors',
  'Management',
]);

/** Default scorecard config thresholds for ROAS Vector (months to 50% ROAS, lower is better). */
const ROAS_VECTOR_THRESHOLD_SCORE_3 = 4;
const ROAS_VECTOR_THRESHOLD_SCORE_2 = 12;

const scoreFromLowerBetterThresholds = (value, thresholdScore3, thresholdScore2) => {
  const v = Number(value);
  if (!Number.isFinite(v)) {
    return null;
  }
  if (v < thresholdScore3) {
    return 3;
  }
  if (v < thresholdScore2) {
    return 2;
  }
  return 1;
};

const parseScorecardMonthsFromValueText = (text) => {
  const match = String(text || '').trim().match(/^(\d+)/);
  if (!match) {
    return null;
  }
  const n = parseInt(match[1], 10);
  return Number.isFinite(n) ? n : null;
};

const formatScorecardNumberWithDots = (numericValue) =>
  new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 20,
  }).format(numericValue);

const isInvestorOrManagementScorecardRow = (row) =>
  row.key === 'investors' || row.key === 'management';

const isUaPredictionScorecardRow = (row) =>
  row?.key === 'ua_prediction' || row?.key === 'cac_prediction';

const formatCacPredictionRatioPercent = (detail, row) => {
  let ratio = null;
  if (detail?.ratio != null && Number.isFinite(Number(detail.ratio))) {
    ratio = Number(detail.ratio);
  } else if (row?.valueText != null && row.valueText !== '') {
    const parsed = Number(String(row.valueText).replace(/,/g, ''));
    if (Number.isFinite(parsed)) {
      ratio = parsed;
    }
  }
  if (ratio == null) {
    return '—';
  }
  const pct = ratio * 100;
  return `${formatScorecardNumberWithDots(Math.round(pct * 100) / 100)}%`;
};

const formatCacPredictionR2 = (r2) =>
  r2 != null && Number.isFinite(Number(r2)) ? Number(r2).toFixed(4) : '—';

const CacPredictionRegressionBlock = ({
  title,
  modelLabel,
  equation,
  r2,
  minUaDisplay,
  targetMob,
}) => (
  <Box>
    {title ? (
      <Text fontWeight={'semibold'} fontSize={'sm'} mb={2}>
        {title}
      </Text>
    ) : null}
    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} fontSize={'sm'}>
      {modelLabel ? (
        <Text>
          <Text as={'span'} fontWeight={'semibold'}>
            Model:{' '}
          </Text>
          {modelLabel}
        </Text>
      ) : null}
      <Text>
        <Text as={'span'} fontWeight={'semibold'}>
          R²:{' '}
        </Text>
        {formatCacPredictionR2(r2)}
      </Text>
      <Text gridColumn={{ base: 'auto', md: 'span 2' }}>
        <Text as={'span'} fontWeight={'semibold'}>
          Equation:{' '}
        </Text>
        {equation || '—'}
      </Text>
      <Text gridColumn={{ base: 'auto', md: 'span 2' }}>
        <Text as={'span'} fontWeight={'semibold'}>
          Max marketing @ ≥100% ROAS (MOB {targetMob ?? 12}):{' '}
        </Text>
        {minUaDisplay || '—'}
      </Text>
    </SimpleGrid>
  </Box>
);

const CacPredictionRegressionDetailPanel = ({ detail }) => {
  if (!detail) {
    return null;
  }
  const mob = detail.targetMob ?? 12;
  const bestIsLinear = detail.regressionType === 'linear';
  const showLinearSeparately =
    detail.linear && !bestIsLinear && detail.linear.equation;

  return (
    <Box>
      <CacPredictionRegressionBlock
        title={
          showLinearSeparately
            ? `Best fit (${detail.regressionTypeLabel || detail.regressionType || '—'})`
            : null
        }
        modelLabel={
          showLinearSeparately
            ? null
            : detail.regressionTypeLabel || detail.regressionType || '—'
        }
        equation={detail.equation}
        r2={detail.r2}
        minUaDisplay={detail.minUaFor100RoasDisplay}
        targetMob={mob}
      />
      {showLinearSeparately ? (
        <Box mt={4} pt={4} borderTopWidth={'1px'} borderColor={'gray.200'}>
          <CacPredictionRegressionBlock
            title={'Linear regression'}
            modelLabel={'Linear'}
            equation={detail.linear.equation}
            r2={detail.linear.r2}
            minUaDisplay={detail.linear.minUaFor100RoasDisplay}
            targetMob={mob}
          />
        </Box>
      ) : null}
    </Box>
  );
};

const CollapsibleSectionHeader = ({
  title,
  isOpen,
  onToggle,
  infoTooltip = null,
  trailing = null,
}) => (
  <HStack
    justifyContent={'space-between'}
    alignItems={'flex-start'}
    mb={isOpen ? 4 : 0}
    w={'100%'}
  >
    <HStack
      spacing={1}
      cursor={'pointer'}
      userSelect={'none'}
      w={'fit-content'}
      onClick={onToggle}
    >
      {isOpen ? (
        <ChevronDownIcon boxSize={4} color={'gray.500'} />
      ) : (
        <ChevronRightIcon boxSize={4} color={'gray.500'} />
      )}
      <Heading size={'sm'}>{title}</Heading>
      {infoTooltip ? (
        <Tooltip label={infoTooltip} hasArrow placement={'top'}>
          <InfoOutlineIcon
            ml={1}
            color={'gray.500'}
            boxSize={4}
            cursor={'help'}
            onClick={(event) => event.stopPropagation()}
          />
        </Tooltip>
      ) : null}
    </HStack>
    {trailing ? (
      <Box flexShrink={0} onClick={(event) => event.stopPropagation()}>
        {trailing}
      </Box>
    ) : null}
  </HStack>
);

const formatInvestorManagementScorecardValue = (row, sessionOv) => {
  if (
    sessionOv?.numericValue != null &&
    Number.isFinite(Number(sessionOv.numericValue))
  ) {
    return String(Math.round(Number(sessionOv.numericValue)));
  }
  if (
    row.overrideActive &&
    row.overrideNumericValue != null &&
    Number.isFinite(Number(row.overrideNumericValue))
  ) {
    return String(Math.round(Number(row.overrideNumericValue)));
  }
  const score = sessionOv?.score ?? row.score;
  if (
    score != null &&
    Number.isInteger(Number(score)) &&
    Number(score) >= 1 &&
    Number(score) <= 3
  ) {
    return String(Number(score));
  }
  const trimmed = String(row.valueText ?? '').trim();
  if (/^[123]$/.test(trimmed)) {
    return trimmed;
  }
  return '—';
};

const deriveScorecardOverrideScore = (rowKey, numericValue, scoreFromDropdown) => {
  if (numericValue == null || !Number.isFinite(Number(numericValue))) {
    return scoreFromDropdown;
  }
  const n = Number(numericValue);
  if (rowKey === 'investors' || rowKey === 'management') {
    const rounded = Math.round(n);
    if (rounded >= 1 && rounded <= 3) {
      return rounded;
    }
    return scoreFromDropdown;
  }
  if (rowKey === 'roas_vector') {
    return scoreFromLowerBetterThresholds(
      n,
      ROAS_VECTOR_THRESHOLD_SCORE_3,
      ROAS_VECTOR_THRESHOLD_SCORE_2
    );
  }
  return scoreFromDropdown;
};

const formatScorecardOverrideNumericForRow = (row, numericValue) => {
  const name = row.name;
  const n = Number(numericValue);
  if (row.key === 'roas_vector' || name === 'ROAS Vector') {
    const months = Math.round(n);
    if (months === 1) {
      return '1 month';
    }
    return `${months} months`;
  }
  if (row.key === 'investors' || row.key === 'management') {
    return String(Math.round(n));
  }
  const core = formatScorecardNumberWithDots(n);
  if (
    row.key === 'revenue_growth_yoy' ||
    row.unit === '% YoY' ||
    name === SCORECARD_ROW_REVENUE_GROWTH
  ) {
    const s = String(core).trim();
    if (s.endsWith('%')) {
      return s;
    }
    return `${s} %`;
  }
  const unitLc = String(row.unit || '').toLowerCase();
  const isOpexMultiple =
    unitLc.includes('opex') &&
    (unitLc.includes('x') || unitLc.includes('monthly'));
  if (isOpexMultiple && core !== '—' && core !== '-') {
    return `${core}x`;
  }
  if (
    isUaPredictionScorecardRow(row) ||
    String(row.unit || '').toLowerCase().includes('funding marketing')
  ) {
    return `${formatScorecardNumberWithDots(Math.round(n * 100 * 100) / 100)}%`;
  }
  if (
    row.unit === 'months' ||
    String(row.unit || '').includes('months') ||
    SCORECARD_ROW_NAMES_MONTHS.has(name)
  ) {
    if (/months?$/i.test(String(core))) {
      return core;
    }
    return `${core} months`;
  }
  return core;
};

const SCORECARD_TOOLTIP_RUNOFF_WITH_CASH =
  'Months until the business runs out of cash, modeled from cohort start. Formula: (Starting Cash + Cumulative Revenue from existing cohorts) / (|Cumulative Total Opex| + |Cumulative Twelve repayments on asset tape|). The period where this ratio first drops below 1 marks the runoff horizon. Expenses include Total Opex only (direct marketing spend and COGS are excluded here).';

const SCORECARD_TOOLTIP_RUNOFF_WITHOUT_CASH =
  'Same runoff horizon as "With Cash," but starting cash is excluded from the numerator — the path uses only cumulative revenue from existing cohorts divided by the same expense denominator (|Total Opex| + |Twelve asset-tape repayments|).';

const SCORECARD_TOOLTIP_CAC_PAYBACK =
  'Shows the number of months until modeled ROAS hits 100%. If it never hits 100%, CAC Payback score is 1.';

const SCORECARD_TOOLTIP_ROAS_VECTOR =
  'Shows the number of months until modeled ROAS reaches 50%. Fewer months means marketing spend is recovered faster (front-loaded). If ROAS never reaches 50%, the score is 1.';

const CASHFLOW_ROAS_VECTOR_GROSS_TOOLTIP = 'This is gross ROAS.';

const SCORECARD_TOOLTIP_CASH_BALANCE =
  'Current cash balance (from the Cash Balance field at the top, including any manual override) divided by MOB 0 Total Opex from the cashflow model. Expressed as a multiple of monthly operating expenses. Formula: Cash Balance / |MOB 0 Total Opex|.';

const SCORECARD_TOOLTIP_RUNWAY =
  'Months of runway remaining, modeled from cohort start. Formula: (Cash + Cumulative Revenue from all cohorts + Cumulative Twelve Draw) / (|Cumulative Opex| + Cumulative Twelve Repayment). Expenses include Total Opex + Direct Marketing Spend + COGS. Twelve fundings (Draw) are added to the numerator as they increase available cash; Twelve repayments are added to the denominator as they are outflow obligations. The period where this ratio first drops below 1 marks the runway horizon.';

const SCORECARD_TOOLTIP_REVENUE_YOY =
  'Year-over-year gross revenue growth. Compares the last full calendar month to the same calendar month one year prior. Formula: (Last Full Month Gross Revenue − Same Month Prior Year Gross Revenue) / Same Month Prior Year Gross Revenue, expressed as a percentage. A positive value means revenue grew; a negative value means it declined.';

const SCORECARD_TOOLTIP_BALANCE_SHEET =
  'Current ratio from the cashflow model at MOB −1 (the calendar month immediately before the model\'s anchor month, aligned with "month on books" vs first-of-current-month). Formula: Total Current Assets ÷ Total Current Liabilities for that month. Higher values indicate more short-term liquidity relative to near-term obligations.';

const SCORECARD_TOOLTIP_UA_PREDICTION =
  'Estimates how much UA marketing spend can scale before modeled gross ROAS at MOB 12 falls below 100%. Historical cohort UA spend and MOB-12 gross ROAS are regressed (best fit among linear, exponential, logarithmic, power, and polynomial models). The score value is the ratio of the regression’s max spend at ≥100% ROAS to this deal’s funding marketing spend — shown as a percentage (e.g. 150% means ~1.5× headroom). Higher scores mean more room to grow UA before hitting the ROAS floor.';

const ROAS_COHORT_MONTHS_BACK = 13;
const ROAS_COHORT_DISPLAY_MONTHS = 6;

const TEARSHEET_PREDICTION_MODEL_API_BY_UI = {
  linear: 'linear_prediction',
  markov: 'markov_chain_prediction',
  combined: 'linear_markov_chain_prediction',
};

const buildRoasCohortPerformanceTooltip = ({ predictionModel, includeRefunds }) => {
  const modelApi =
    TEARSHEET_PREDICTION_MODEL_API_BY_UI[predictionModel] ||
    'linear_markov_chain_prediction';
  const refundsLabel = includeRefunds ? 'with refunds included' : 'with refunds excluded';
  return `We fetch ${ROAS_COHORT_MONTHS_BACK} months of cohort history but display only the latest ${ROAS_COHORT_DISPLAY_MONTHS} cohort months (most recent first). Predicted ROAS uses ${modelApi} (${refundsLabel}). Configure prediction model and refunds in Tearsheet settings.`;
};

const formatRunoffDebugNumber = (v) =>
  v != null && typeof v === 'number' && Number.isFinite(v)
    ? v.toLocaleString(undefined, { maximumFractionDigits: 2 })
    : '—';

const formatRunoffDebugInteger = (v) =>
  v != null && typeof v === 'number' && Number.isFinite(v)
    ? v.toLocaleString(undefined, { maximumFractionDigits: 0 })
    : '—';

/** Chakra color token for scorecard Score column (1–3). */
const scorecardScoreColumnColor = (score) => {
  if (score === 3) {
    return 'green.600';
  }
  if (score === 2) {
    return 'yellow.700';
  }
  if (score === 1) {
    return 'red.600';
  }
  return 'gray.400';
};

const cashflowMetricLabelIsCurrency = (label) => {
  if (!label) {
    return false;
  }
  return (
    /Runoff Revenue/i.test(label) ||
    /Runway Revenue/i.test(label) ||
    label === CASHFLOW_METRIC_ANNUAL_RUN_RATE_LABEL ||
    label === 'Max Exposure' ||
    /Gross revenue/i.test(label)
  );
};

const TearsheetNew = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [pdfGeneratingId, setPdfGeneratingId] = useState(null);
  const [tearsheets, setTearsheets] = useState([]);
  const [showCreateMode, setShowCreateMode] = useState(false);
  const [createFormOptions, setCreateFormOptions] = useState({
    deals: [],
    installments: [],
  });
  const [fetchResult, setFetchResult] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [mode, setMode] = useState('deal');
  const [selectedDealId, setSelectedDealId] = useState('');
  const [selectedInstallmentId, setSelectedInstallmentId] = useState('');
  const [marketingSpendAmount, setMarketingSpendAmount] = useState('');
  const [requestedFundingPct, setRequestedFundingPct] = useState('');
  const [cashBalanceOverride, setCashBalanceOverride] = useState(null);
  const [accountingCashFromBq, setAccountingCashFromBq] = useState(null);
  const [cashBalanceOverrideDraft, setCashBalanceOverrideDraft] = useState('');
  const [cashBalanceOverrideComment, setCashBalanceOverrideComment] = useState('');
  const [savedConfigState, setSavedConfigState] = useState({
    marketingMoMGrowthPct: 10,
    xirrPct: 20,
    numberOfRepayments: 24,
    gracePeriod: 1,
    predictionModel: 'combined',
    includeRefunds: true,
  });
  const [draftConfigState, setDraftConfigState] = useState({
    marketingMoMGrowthPct: 10,
    xirrPct: 20,
    numberOfRepayments: 24,
    gracePeriod: 1,
    predictionModel: 'combined',
    includeRefunds: true,
  });
  const [roasGrossNetView, setRoasGrossNetView] = useState('gross');

  const [cashflowDetailTableOpen, setCashflowDetailTableOpen] = useState(false);
  const [runoffDebugOpen, setRunoffDebugOpen] = useState(false);
  const [uaPredictionOpen, setUaPredictionOpen] = useState(false);
  const [covenantsOpen, setCovenantsOpen] = useState(false);
  const [roasCohortOpen, setRoasCohortOpen] = useState(false);
  const [cashflowModelOpen, setCashflowModelOpen] = useState(false);
  const [cashflowEmbed, setCashflowEmbed] = useState({
    loading: false,
    error: null,
    results: null,
  });

  const cashflowScenarioIdRef = useRef(null);
  const isSavedRef = useRef(false);

  const toast = useToast();
  const navigate = useNavigate();
  const { customerId, tearsheetId: savedTearsheetId } = useParams();
  const { getUserName } = useContext(CockpitUsersContext);
  const columnHelper = createColumnHelper();
  const saveModalDisclosure = useDisclosure();
  const cashOverrideDrawerDisclosure = useDisclosure();
  const scorecardOverrideDrawerDisclosure = useDisclosure();
  const configurationDrawerDisclosure = useDisclosure();
  const deleteConfirmModalDisclosure = useDisclosure();

  const [pendingDeleteTearsheet, setPendingDeleteTearsheet] = useState({
    id: null,
    displayName: '',
  });

  const [scorecardScoreOverrides, setScorecardScoreOverrides] = useState({});
  const [scorecardOverrideRowKey, setScorecardOverrideRowKey] = useState(null);
  const [scorecardOverrideDraftScore, setScorecardOverrideDraftScore] = useState('');
  const [scorecardOverrideDraftReason, setScorecardOverrideDraftReason] = useState('');
  const [scorecardOverrideDraftNumericValue, setScorecardOverrideDraftNumericValue] =
    useState('');
  const [scorecardOverrideDrawerViewOnly, setScorecardOverrideDrawerViewOnly] =
    useState(false);
  const [savedScorecardFinalScore, setSavedScorecardFinalScore] = useState(null);

  const loadTearsheets = async () => {
    setIsLoading(true);
    try {
      const list = await getTearsheetListApi({
        customerId,
        tearsheetVersion: TEARSHEET_VERSION.V2,
      });
      setTearsheets(list);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load new tearsheets',
        status: 'error',
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadTearsheets();
  }, [customerId]);

  useEffect(() => {
    if (!customerId) {
      setAccountingCashFromBq(null);
      return undefined;
    }
    setAccountingCashFromBq(null);
    let cancelled = false;
    (async () => {
      try {
        const data = await getTearsheetAccountingCashBalanceApi({ customerId });
        if (cancelled) {
          return;
        }
        setAccountingCashFromBq({
          amount: data?.amount ?? 0,
          asOfDate: data?.asOfDate ?? null,
        });
      } catch (err) {
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  useEffect(() => {
    cashflowScenarioIdRef.current = fetchResult?.cashflowScenarioId ?? null;
  }, [fetchResult?.cashflowScenarioId]);

  useEffect(() => {
    setCashflowDetailTableOpen(false);
  }, [fetchResult?.cashflowScenarioId]);

  useEffect(() => {
    const scenarioId = fetchResult?.cashflowScenarioId;
    if (!scenarioId || !customerId) {
      setCashflowEmbed({ loading: false, error: null, results: null });
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setCashflowEmbed({ loading: true, error: null, results: null });
      try {
        const doc = await getTearsheetCashflowScenarioApi({
          customerId,
          scenarioId,
        });
        const rawRes = doc?.result;
        if (cancelled) {
          return;
        }
        if (rawRes == null || rawRes === '') {
          setCashflowEmbed({
            loading: false,
            error: null,
            results: null,
          });
          return;
        }
        const parsed =
          typeof rawRes === 'string' ? JSON.parse(rawRes) : rawRes;
        const normalized = normalizeStoredCashflowResult(parsed);
        setCashflowEmbed({
          loading: false,
          error: null,
          results: normalized,
        });
      } catch (error) {
        if (!cancelled) {
          setCashflowEmbed({
            loading: false,
            error,
            results: null,
          });
          toast({
            title: 'Error',
            description: 'Could not load saved cashflow results.',
            status: 'error',
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchResult, customerId]);

  useEffect(() => {
    isSavedRef.current = isSaved;
  }, [isSaved]);

  useEffect(() => {
    if (!selectedDealId || !createFormOptions?.deals?.length) return;
    const deal = createFormOptions.deals.find((d) => d.id === selectedDealId);
    if (deal?.fundingPct != null) {
      setRequestedFundingPct(normalizeNumericInputValue(deal.fundingPct));
    } else {
      setRequestedFundingPct('');
    }
  }, [selectedDealId]);

  const onDeleteTearsheet = async (tearsheetId) => {
    setIsLoading(true);
    try {
      await deleteTearsheetApi(customerId, tearsheetId);
      await loadTearsheets();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete tearsheet',
        status: 'error',
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const downloadSavedTearsheetZip = async (tearsheetId, listRow) => {
    if (!customerId || pdfGeneratingId != null) {
      return;
    }
    setPdfGeneratingId(tearsheetId);
    const mountPdf = document.createElement('div');
    mountPdf.style.cssText =
      'position:fixed;left:-9999px;top:0;width:900px;pointer-events:none;';
    document.body.appendChild(mountPdf);
    let pdfRoot = null;
    const mountCsv = document.createElement('div');
    mountCsv.style.cssText =
      'position:fixed;left:-9999px;top:0;width:1200px;pointer-events:none;';
    document.body.appendChild(mountCsv);
    let csvRoot = null;
    try {
      const response = await getTearsheetApi({ customerId, tearsheetId });
      if (!response?.payloadJson) {
        toast({
          title: 'Error',
          description: 'Saved tearsheet data not found',
          status: 'error',
        });
        return;
      }
      let payload;
      try {
        payload = JSON.parse(response.payloadJson);
      } catch {
        toast({
          title: 'Error',
          description: 'Invalid saved tearsheet data',
          status: 'error',
        });
        return;
      }

      const zip = new JSZip();
      const roasCsv = buildRoasCohortCsvString(payload);
      if (roasCsv) {
        zip.file('roas-cohort.csv', roasCsv);
      }

      const runoffRunwayCsv = buildRunoffRunwayDebugCsvString(payload);
      if (runoffRunwayCsv) {
        zip.file('runoff-runway-calculation.csv', runoffRunwayCsv);
      }

      if (payload.cashflowScenarioId) {
        try {
          const doc = await getTearsheetCashflowScenarioApi({
            customerId,
            scenarioId: payload.cashflowScenarioId,
          });
          const rawRes = doc?.result;
          if (rawRes != null && rawRes !== '') {
            const parsed =
              typeof rawRes === 'string' ? JSON.parse(rawRes) : rawRes;
            const normalized = normalizeStoredCashflowResult(parsed);
            const repaymentsCsv = buildRepaymentsPerCohortCsvString(normalized);
            if (repaymentsCsv) {
              zip.file('repayments-per-cohort.csv', repaymentsCsv);
            }
            const cohortRevenueCsv =
              buildCohortRevenueBreakdownCsvString(normalized);
            if (cohortRevenueCsv) {
              zip.file('cohort-revenue-breakdown.csv', cohortRevenueCsv);
            }
            let requestParsed = null;
            try {
              const rawReq = doc?.request;
              if (rawReq != null && rawReq !== '') {
                requestParsed =
                  typeof rawReq === 'string' ? JSON.parse(rawReq) : rawReq;
              }
            } catch {
              requestParsed = null;
            }
            const prefill = buildCashflowCsvPrefillFromScenarioRequest(
              requestParsed,
              customerId
            );
            const csvHarnessRef = createRef();
            csvRoot = createRoot(mountCsv);
            const appTheme =
              process.env.REACT_APP_IS_NEW_DESIGN === 'true'
                ? themeV2
                : themeV1;
            csvRoot.render(
              <MemoryRouter>
                <ChakraProvider theme={appTheme}>
                  <TearsheetCashflowCsvHarness
                    ref={csvHarnessRef}
                    normalizedResult={normalized}
                    csvExportPrefillValues={prefill}
                  />
                </ChakraProvider>
              </MemoryRouter>
            );
            await new Promise((r) => requestAnimationFrame(r));
            await new Promise((r) => requestAnimationFrame(r));
            await new Promise((r) => requestAnimationFrame(r));
            const matrix =
              csvHarnessRef.current?.getFullCashflowCsvMatrix?.() ?? [];
            if (matrix.length > 0) {
              zip.file('cashflow-model.csv', Papa.unparse(matrix));
            }
          }
        } catch {
          toast({
            title: 'Warning',
            description:
              'Could not add cashflow-model.csv (scenario export failed).',
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
        }
      }

      pdfRoot = createRoot(mountPdf);
      const pdfTheme =
        process.env.REACT_APP_IS_NEW_DESIGN === 'true' ? themeV2 : themeV1;
      pdfRoot.render(
        <ChakraProvider theme={pdfTheme}>
          <TearsheetNewPdfDocument
            payload={payload}
            savedAt={listRow?.createdAt || listRow?.date || null}
          />
        </ChakraProvider>
      );
      await new Promise((requestFrame) => requestAnimationFrame(requestFrame));
      await new Promise((requestFrame) => requestAnimationFrame(requestFrame));
      const getPdfTargetElement = () =>
        mountPdf.querySelector('[data-tearsheet-new-pdf-root]') ||
        mountPdf.firstElementChild;
      const pdfDoc = await generatePDF(getPdfTargetElement, {
        method: 'build',
        page: { margin: Margin.SMALL },
      });
      zip.file('tearsheet.pdf', pdfDoc.output('blob'));

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveAs(
        zipBlob,
        buildTearsheetNewZipFilename(
          payload,
          listRow?.createdAt || listRow?.date
        )
      );
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to build download package',
        status: 'error',
      });
    } finally {
      if (pdfRoot) {
        pdfRoot.unmount();
      }
      if (mountPdf.parentNode) {
        mountPdf.parentNode.removeChild(mountPdf);
      }
      if (csvRoot) {
        csvRoot.unmount();
      }
      if (mountCsv.parentNode) {
        mountCsv.parentNode.removeChild(mountCsv);
      }
      setPdfGeneratingId(null);
    }
  };

  const openDeleteTearsheetConfirm = (tearsheetId, displayName) => {
    setPendingDeleteTearsheet({
      id: tearsheetId,
      displayName: displayName || 'this tearsheet',
    });
    deleteConfirmModalDisclosure.onOpen();
  };

  const closeDeleteTearsheetConfirm = () => {
    deleteConfirmModalDisclosure.onClose();
    setPendingDeleteTearsheet({ id: null, displayName: '' });
  };

  const confirmDeleteTearsheet = async () => {
    if (!pendingDeleteTearsheet.id) {
      return;
    }
    try {
      await onDeleteTearsheet(pendingDeleteTearsheet.id);
    } catch {
      return;
    }
    closeDeleteTearsheetConfirm();
  };

  const enterCreateMode = () => {
    setShowCreateMode(true);
    setIsSaved(false);
    setSaveName('');
    setFetchResult(null);
    setMode('deal');
    setSelectedDealId('');
    setSelectedInstallmentId('');
    setRequestedFundingPct('');
    setMarketingSpendAmount('');
    setCashBalanceOverride(null);
    setCashBalanceOverrideComment('');
    setCashBalanceOverrideDraft('');
    setSavedScorecardFinalScore(null);
    setScorecardScoreOverrides({});
    setScorecardOverrideDraftNumericValue('');
    setScorecardOverrideDrawerViewOnly(false);
    setCreateFormOptions({
      deals: [],
      installments: [],
    });
    configurationDrawerDisclosure.onClose();
    cashflowScenarioIdRef.current = null;
    loadCreateFormOptions();
  };

  const loadSavedTearsheet = async (tearsheetId) => {
    setIsLoading(true);
    try {
      const response = await getTearsheetApi({ customerId, tearsheetId });
      if (!response?.payloadJson) {
        toast({
          title: 'Error',
          description: 'Saved tearsheet data not found',
          status: 'error',
        });
        return;
      }
      const payload = JSON.parse(response.payloadJson);
      const savedMode = payload.mode || 'deal';
      const savedDealId =
        payload.dealId != null && payload.dealId !== ''
          ? String(payload.dealId)
          : '';
      const savedInstallmentId =
        payload.installmentId != null && payload.installmentId !== ''
          ? String(payload.installmentId)
          : '';
      setSaveName(payload.name || '');
      setMode(savedMode);
      setSelectedDealId(savedDealId);
      setSelectedInstallmentId(savedInstallmentId);
      setRequestedFundingPct(
        payload.requestedFundingPct != null
          ? String(payload.requestedFundingPct)
          : ''
      );
      setMarketingSpendAmount(
        payload.marketingSpendAmount != null
          ? String(payload.marketingSpendAmount)
          : ''
      );
      if (payload.cashBalanceAmount != null) {
        setCashBalanceOverride(payload.cashBalanceAmount);
      }
      const loadedScorecard = payload.cashflowScorecard ?? [];
      setFetchResult({
        requestedFundingAmount: payload.requestedFundingAmount ?? 0,
        requestedFundingPct: payload.requestedFundingPct ?? 0,
        marketingSpendAmount: payload.marketingSpendAmount ?? 0,
        cashBalance: {
          amount: payload.cashBalanceAmount ?? 0,
          asOfDate: payload.cashBalanceAsOfDate ?? null,
        },
        resultRows: payload.resultRows ?? [],
        roasTargets: payload.roasTargets ?? [],
        dealConstraints: payload.dealConstraints ?? null,
        currentExposure: payload.currentExposure ?? null,
        nextRepaymentAmount: payload.nextRepaymentAmount ?? null,
        nextRepaymentDate: payload.nextRepaymentDate ?? null,
        futureExposure: payload.futureExposure ?? null,
        cashflowScenarioId: payload.cashflowScenarioId ?? null,
        scorecardConfigVersionId: payload.scorecardConfigVersionId ?? null,
        scorecardConfigVersion: payload.scorecardConfigVersion ?? null,
        cashflowScorecard: loadedScorecard,
        cashflowMetrics: payload.cashflowMetrics ?? [],
        cashflowRoasVector: payload.cashflowRoasVector ?? [],
        annualRunRate: finiteNumberOrNull(payload.annualRunRate),
        exposureToRunRatePct: finiteNumberOrNull(payload.exposureToRunRatePct),
        cashflowRunoffDebugWithCash:
          payload.cashflowRunoffDebugWithCash ?? null,
        cashflowRunoffDebugWithoutCash:
          payload.cashflowRunoffDebugWithoutCash ?? null,
        cashflowRunwayDebug: payload.cashflowRunwayDebug ?? null,
        roasCohort: payload.roasCohort ?? null,
        cacPrediction: payload.cacPrediction ?? null,
        scorecardGradeThresholds: payload.scorecardGradeThresholds ?? null,
      });
      const overridesFromSaved = {};
      for (const r of loadedScorecard) {
        if (r.overrideActive && r.key != null) {
          const num =
            r.overrideNumericValue != null
              ? Number(r.overrideNumericValue)
              : null;
          overridesFromSaved[r.key] = {
            score: r.score != null ? Number(r.score) : null,
            reason: r.overrideReason != null ? String(r.overrideReason) : '',
            numericValue:
              num != null && Number.isFinite(num) ? num : null,
          };
        }
      }
      setScorecardScoreOverrides(overridesFromSaved);
      setRoasGrossNetView(
        payload.roasGrossNetView === 'net' ? 'net' : 'gross'
      );
      setSavedScorecardFinalScore(payload.scorecardFinalScore ?? null);
      setIsSaved(true);
      setShowCreateMode(true);
      try {
        await loadTearsheetFormOptions({
          formMode: savedMode,
          dealId: savedDealId || null,
          installmentId: savedInstallmentId || null,
        });
      } catch {
        // Options fetch failure is non-fatal; saved snapshot data is already loaded.
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load saved tearsheet',
        status: 'error',
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (savedTearsheetId && customerId) {
      loadSavedTearsheet(savedTearsheetId);
    }
  }, [savedTearsheetId, customerId]);

  const loadTearsheetFormOptions = async ({
    formMode,
    dealId,
    installmentId,
  }) => {
    const response = await fetchTearsheetNewApi({
      customerId,
      data: {
        mode: formMode || 'deal',
        dealId: dealId || null,
        installmentId: installmentId || null,
        requestedFundingPct: null,
        marketingSpendAmount: null,
        cashBalanceOverride: null,
        skipInstallments: formMode !== 'installment',
      },
    });
    setCreateFormOptions({
      deals: response.deals || [],
      installments: response.installments || [],
    });
    return response;
  };

  const loadCreateFormOptions = async () => {
    try {
      const response = await loadTearsheetFormOptions({
        formMode: 'deal',
        dealId: null,
        installmentId: null,
      });
      if (response.accountingCashPreview != null) {
        setAccountingCashFromBq({
          amount: response.accountingCashPreview.amount ?? 0,
          asOfDate: response.accountingCashPreview.asOfDate ?? null,
        });
      }
      if ((response.deals || []).length > 0) {
        setSelectedDealId(response.deals[0].id);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load deal and installment list',
        status: 'error',
      });
      throw error;
    }
  };

  const dealsOptions = useMemo(() => {
    return (createFormOptions.deals || []).map((deal) => ({
      value: deal.id,
      label: deal.name,
    }));
  }, [createFormOptions]);

  const installmentsOptions = useMemo(() => {
    return (createFormOptions.installments || []).map((installment) => ({
      value: installment.id,
      label: `Cohort: ${installment.cohortStartDate || '-'} / TransferDate: ${installment.transferDate || '-'}`,
    }));
  }, [createFormOptions]);

  const runFetch = async () => {
    setIsFetching(true);
    try {
      const predictionModelApiByUi = {
        linear: 'linear_prediction',
        markov: 'markov_chain_prediction',
        combined: 'linear_markov_chain_prediction',
      };
      const payload = {
        mode,
        dealId: selectedDealId || null,
        installmentId: selectedInstallmentId || null,
        requestedFundingPct:
          mode === 'deal'
            ? parseFormattedNumericInput(requestedFundingPct)
            : null,
        marketingSpendAmount:
          mode === 'deal'
            ? parseFormattedNumericInput(marketingSpendAmount)
            : null,
        cashBalanceOverride:
          cashBalanceOverride !== null ? Number(cashBalanceOverride) : null,
        accountingCashBalance:
          accountingCashFromBq != null &&
          Number.isFinite(accountingCashFromBq.amount)
            ? accountingCashFromBq.amount
            : null,
        accountingCashAsOfDate: accountingCashFromBq?.asOfDate ?? null,
        marketingMoMGrowthPct: savedConfigState.marketingMoMGrowthPct,
        xirrPct: savedConfigState.xirrPct,
        numberOfRepayments: savedConfigState.numberOfRepayments,
        gracePeriod: savedConfigState.gracePeriod,
        predictionModel:
          predictionModelApiByUi[savedConfigState.predictionModel] ||
          'linear_markov_chain_prediction',
        includeRefunds: savedConfigState.includeRefunds,
        cashflowScenarioId: fetchResult?.cashflowScenarioId || null,
      };
      const response = await fetchTearsheetNewApi({
        customerId,
        data: payload,
      });

      setCreateFormOptions({
        deals: response.deals || [],
        installments: response.installments || [],
      });
      setFetchResult(response);
      setScorecardScoreOverrides({});
      if (mode === 'deal') {
        if (response.requestedFundingPct !== undefined) {
          setRequestedFundingPct(
            normalizeNumericInputValue(response.requestedFundingPct)
          );
        }
        if (response.marketingSpendAmount !== undefined) {
          setMarketingSpendAmount(
            normalizeNumericInputValue(response.marketingSpendAmount)
          );
        }
      }
      if (!selectedDealId && response.deals?.length > 0) {
        setSelectedDealId(response.deals[0].id);
      }
      if (response.cashflowFetchWarning) {
        toast({
          title: 'Tearsheet loaded without cashflow model',
          description: response.cashflowFetchWarning,
          status: 'warning',
          duration: 14000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Success',
          description: 'Tearsheet data fetched',
          status: 'success',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch tearsheet data',
        status: 'error',
      });
      throw error;
    } finally {
      setIsFetching(false);
    }
  };

  const onOpenSaveModal = () => {
    setSaveName('');
    saveModalDisclosure.onOpen();
  };

  const onOpenConfiguration = () => {
    setDraftConfigState(savedConfigState);
    configurationDrawerDisclosure.onOpen();
  };

  const onSaveConfiguration = () => {
    setSavedConfigState(draftConfigState);
    configurationDrawerDisclosure.onClose();
    toast({
      title: 'Success',
      description: 'Configuration changes saved',
      status: 'success',
    });
  };

  const onSaveTearsheet = async () => {
    if (!fetchResult?.resultRows?.length) {
      return;
    }
    setIsSaving(true);
    try {
      let annualForSave = computeAnnualRunrateFromCashflowItems(
        cashflowEmbed.results?.items
      );
      if (annualForSave == null || !Number.isFinite(annualForSave)) {
        annualForSave = finiteNumberOrNull(fetchResult?.annualRunRate);
      }
      const feSave = fetchResult?.futureExposure ?? null;
      const exposureToRunRatePctSave =
        annualForSave != null &&
        Number.isFinite(annualForSave) &&
        feSave != null &&
        Number.isFinite(feSave) &&
        annualForSave !== 0
          ? Math.round((feSave / annualForSave) * 100 * 100) / 100
          : null;
      const mergedScorecard = mergeCashflowScorecardForSave(
        fetchResult.cashflowScorecard || [],
        scorecardScoreOverrides
      );
      const payload = {
        name: saveName,
        mode,
        dealId: selectedDealId || null,
        installmentId: selectedInstallmentId || null,
        requestedFundingAmount: Number(fetchResult.requestedFundingAmount || 0),
        requestedFundingPct: Number(fetchResult.requestedFundingPct || 0),
        marketingSpendAmount: Number(fetchResult.marketingSpendAmount || 0),
        cashBalanceAmount: Number(fetchResult.cashBalance?.amount || 0),
        cashBalanceAsOfDate: fetchResult.cashBalance?.asOfDate || null,
        resultRows: fetchResult.resultRows || [],
        roasTargets: fetchResult.roasTargets || [],
        dealConstraints: fetchResult.dealConstraints || null,
        currentExposure: fetchResult.currentExposure || null,
        nextRepaymentAmount: fetchResult.nextRepaymentAmount || null,
        nextRepaymentDate: fetchResult.nextRepaymentDate || null,
        futureExposure: fetchResult.futureExposure || null,
        cashflowScenarioId: fetchResult.cashflowScenarioId || null,
        scorecardConfigVersionId:
          fetchResult.scorecardConfigVersionId || null,
        scorecardConfigVersion: fetchResult.scorecardConfigVersion ?? null,
        scorecardFinalScore: scorecardFinalScore ?? null,
        cashflowScorecard: mergedScorecard,
        cashflowMetrics: fetchResult.cashflowMetrics || [],
        cashflowRoasVector: fetchResult.cashflowRoasVector || [],
        annualRunRate: annualForSave,
        exposureToRunRatePct: exposureToRunRatePctSave,
        cashflowRunoffDebugWithCash:
          fetchResult.cashflowRunoffDebugWithCash ?? null,
        cashflowRunoffDebugWithoutCash:
          fetchResult.cashflowRunoffDebugWithoutCash ?? null,
        cashflowRunwayDebug: fetchResult.cashflowRunwayDebug ?? null,
        roasCohort: fetchResult.roasCohort ?? null,
        roasGrossNetView: roasGrossNetView === 'net' ? 'net' : 'gross',
        scorecardGradeThresholds: fetchResult.scorecardGradeThresholds ?? null,
        cacPrediction: fetchResult.cacPrediction ?? null,
      };
      await saveTearsheetNewApi({
        customerId,
        data: payload,
      });
      setFetchResult((prev) =>
        prev
          ? {
              ...prev,
              annualRunRate: annualForSave,
              exposureToRunRatePct: exposureToRunRatePctSave,
              cashflowScorecard: mergedScorecard,
              cashflowRunoffDebugWithCash:
                fetchResult.cashflowRunoffDebugWithCash ?? null,
              cashflowRunoffDebugWithoutCash:
                fetchResult.cashflowRunoffDebugWithoutCash ?? null,
              cashflowRunwayDebug: fetchResult.cashflowRunwayDebug ?? null,
            }
          : prev
      );
      saveModalDisclosure.onClose();
      setIsSaved(true);
      toast({
        title: 'Success',
        description: 'Tearsheet saved successfully',
        status: 'success',
      });
      await loadTearsheets();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save tearsheet',
        status: 'error',
      });
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const formatNumberWithDots = (numericValue) =>
    new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(numericValue);

  /** Whole dollars for cashflow KPI currency rows (thousands separators, no cents). */
  const formatCashflowCurrencyWholeDollars = (numericValue) =>
    new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numericValue);

  /** KPI/scorecard strings from the API (plain decimals; optional trailing *). */
  const formatCashflowNumericDisplayText = (valueText, options = {}) => {
    const { currency = false } = options;
    if (valueText == null || valueText === '') {
      return '—';
    }
    const s = String(valueText).trim();
    if (s === '—' || s === '-') {
      return s;
    }
    const asterisk = s.endsWith('*');
    const core = asterisk ? s.slice(0, -1).trim() : s;
    if (/MOB\s/i.test(core) || /[()]/.test(core)) {
      return s;
    }
    const num = Number(core.replace(/[$,]/g, ''));
    if (!Number.isFinite(num)) {
      return s;
    }
    const formatted = currency
      ? formatCashflowCurrencyWholeDollars(num)
      : formatNumberWithDots(num);
    if (!currency) {
      return asterisk ? `${formatted}*` : formatted;
    }
    const dollars = `$${formatted}`;
    return asterisk ? `${dollars}*` : dollars;
  };

  const getScorecardNameTooltip = (rowName) => {
    if (rowName === 'Runoff (With Cash)') {
      return SCORECARD_TOOLTIP_RUNOFF_WITH_CASH;
    }
    if (rowName === 'Runoff (Without Cash)') {
      return SCORECARD_TOOLTIP_RUNOFF_WITHOUT_CASH;
    }
    if (rowName === 'CAC Payback') {
      return SCORECARD_TOOLTIP_CAC_PAYBACK;
    }
    if (rowName === 'ROAS Vector') {
      return SCORECARD_TOOLTIP_ROAS_VECTOR;
    }
    if (rowName === 'Cash Balance' || rowName === 'Liquid Value') {
      return SCORECARD_TOOLTIP_CASH_BALANCE;
    }
    if (rowName === 'Runway') {
      return SCORECARD_TOOLTIP_RUNWAY;
    }
    if (
      rowName === 'Revenue Growth (YoY)' ||
      rowName === 'Revenue YoY' ||
      rowName === 'Revenue YOY'
    ) {
      return SCORECARD_TOOLTIP_REVENUE_YOY;
    }
    if (rowName === 'Balance Sheet') {
      return SCORECARD_TOOLTIP_BALANCE_SHEET;
    }
    if (rowName === 'UA Prediction' || rowName === 'CAC Prediction') {
      return SCORECARD_TOOLTIP_UA_PREDICTION;
    }
    return '';
  };

  const formatScorecardValueCell = (row) => {
    const name = row.name;
    const raw = row.valueText ?? '';

    const sessionOv = row.key != null ? scorecardScoreOverrides[row.key] : undefined;

    if (isInvestorOrManagementScorecardRow(row)) {
      return formatInvestorManagementScorecardValue(row, sessionOv);
    }

    if (
      sessionOv?.numericValue != null &&
      Number.isFinite(Number(sessionOv.numericValue))
    ) {
      return formatScorecardOverrideNumericForRow(
        row,
        Number(sessionOv.numericValue)
      );
    }

    if (
      row.overrideActive &&
      row.overrideNumericValue != null &&
      Number.isFinite(Number(row.overrideNumericValue))
    ) {
      return formatScorecardOverrideNumericForRow(
        row,
        Number(row.overrideNumericValue)
      );
    }

    if (
      row.kind === 'qualitative' ||
      SCORECARD_ROW_NAMES_STRING_VALUE.has(name)
    ) {
      const t = String(raw).trim();
      return t === '' ? '—' : t;
    }

    const formatOverrideNumericForRow = (n) =>
      formatScorecardOverrideNumericForRow(row, n);

    let core;
    core = formatCashflowNumericDisplayText(raw);

    if (core === '—' || core === '-' || core === '') {
      return '—';
    }

    if (
      row.key === 'revenue_growth_yoy' ||
      row.unit === '% YoY' ||
      name === SCORECARD_ROW_REVENUE_GROWTH
    ) {
      const s = String(core).trim();
      if (s.endsWith('%')) {
        return s;
      }
      return `${s} %`;
    }

    const unitLc = String(row.unit || '').toLowerCase();
    const isOpexMultiple =
      unitLc.includes('opex') &&
      (unitLc.includes('x') || unitLc.includes('monthly'));
    if (isOpexMultiple && core !== '—' && core !== '-') {
      return `${core}x`;
    }
    if (
      isUaPredictionScorecardRow(row) ||
      String(row.unit || '').toLowerCase().includes('funding marketing')
    ) {
      if (core === '—' || core === '-') {
        return '—';
      }
      const ratio = Number(String(core).replace(/,/g, ''));
      if (!Number.isFinite(ratio)) {
        return '—';
      }
      return `${formatScorecardNumberWithDots(Math.round(ratio * 100 * 100) / 100)}%`;
    }
    if (
      row.unit === 'months' ||
      String(row.unit || '').includes('months') ||
      SCORECARD_ROW_NAMES_MONTHS.has(name)
    ) {
      if (/months?$/i.test(String(core))) {
        return core;
      }
      return `${core} months`;
    }

    return core;
  };

  const formatRunoffDebugCurrency = (v) => {
    const n = formatRunoffDebugInteger(v);
    if (n === '—') {
      return n;
    }
    return `$${n}`;
  };

  const normalizeNumericInputValue = (rawValue, options = {}) => {
    const { allowNegative = false } = options;
    const cleanedValue = String(rawValue || '').replace(/,/g, '');
    if (cleanedValue === '') {
      return '';
    }

    let sign = '';
    let digitsPart = cleanedValue;
    if (allowNegative) {
      if (cleanedValue === '-') {
        return '-';
      }
      if (cleanedValue.startsWith('-')) {
        sign = '-';
        digitsPart = cleanedValue.slice(1);
      }
    }

    const sanitizedValue = digitsPart.replace(/[^0-9.]/g, '');
    if (sanitizedValue === '' && sign === '-') {
      return '-';
    }
    if (sanitizedValue === '') {
      return '';
    }

    const hasTrailingDot = sanitizedValue.endsWith('.');
    const [integerPartRaw, ...decimalParts] = sanitizedValue.split('.');
    const integerPart = integerPartRaw || '0';
    const decimalPart = decimalParts.join('');
    const formattedIntegerPart = formatNumberWithDots(Number(integerPart));

    let out;
    if (hasTrailingDot && decimalPart === '') {
      out = `${formattedIntegerPart}.`;
    } else if (decimalPart !== '') {
      out = `${formattedIntegerPart}.${decimalPart}`;
    } else {
      out = formattedIntegerPart;
    }
    return sign ? `${sign}${out}` : out;
  };

  const parseFormattedNumericInput = (formattedValue) => {
    const cleanedValue = String(formattedValue || '').replace(/,/g, '');
    if (cleanedValue === '') {
      return null;
    }
    const numericValue = Number(cleanedValue);
    if (Number.isNaN(numericValue)) {
      return null;
    }
    return numericValue;
  };

  const openCashOverrideDrawer = () => {
    const baseAmount =
      cashBalanceOverride !== null
        ? cashBalanceOverride
        : accountingCashFromBq != null &&
            Number.isFinite(accountingCashFromBq.amount)
          ? accountingCashFromBq.amount
          : fetchResult?.cashBalance?.amount ?? 0;
    setCashBalanceOverrideDraft(
      normalizeNumericInputValue(String(baseAmount ?? ''))
    );
    setCashBalanceOverrideComment('');
    cashOverrideDrawerDisclosure.onOpen();
  };

  const applyCashOverride = () => {
    const parsed = parseFormattedNumericInput(cashBalanceOverrideDraft);
    if (cashBalanceOverrideDraft === '' || parsed === null) {
      setCashBalanceOverride(null);
    } else {
      setCashBalanceOverride(parsed);
    }
    cashOverrideDrawerDisclosure.onClose();
  };

  const openScorecardOverrideDrawer = (row) => {
    const viewOnly = Boolean(isSaved && row.overrideActive);
    setScorecardOverrideDrawerViewOnly(viewOnly);
    const existing = scorecardScoreOverrides[row.key];
    setScorecardOverrideRowKey(row.key);
    setScorecardOverrideDraftScore(
      existing?.score != null
        ? String(existing.score)
        : row.score != null
          ? String(row.score)
          : ''
    );
    setScorecardOverrideDraftReason(
      existing?.reason ?? row.overrideReason ?? ''
    );
    const numFromExisting = existing?.numericValue;
    const numFromRow = row.overrideNumericValue;
    let num =
      numFromExisting != null && Number.isFinite(Number(numFromExisting))
        ? Number(numFromExisting)
        : numFromRow != null && Number.isFinite(Number(numFromRow))
          ? Number(numFromRow)
          : null;
    if (num == null && row.key === 'roas_vector') {
      num = parseScorecardMonthsFromValueText(row.valueText);
    }
    if (
      num == null &&
      (row.key === 'investors' || row.key === 'management') &&
      row.score != null
    ) {
      num = Number(row.score);
    }
    setScorecardOverrideDraftNumericValue(
      num != null
        ? normalizeNumericInputValue(String(num), { allowNegative: true })
        : ''
    );
    scorecardOverrideDrawerDisclosure.onOpen();
  };

  const applyScorecardScoreOverride = () => {
    if (scorecardOverrideDrawerViewOnly) {
      scorecardOverrideDrawerDisclosure.onClose();
      return;
    }
    const parsed = parseInt(scorecardOverrideDraftScore, 10);
    const parsedNumeric = parseFormattedNumericInput(
      scorecardOverrideDraftNumericValue
    );
    setScorecardScoreOverrides((prev) => {
      if (!scorecardOverrideDraftScore || isNaN(parsed)) {
        const next = { ...prev };
        delete next[scorecardOverrideRowKey];
        return next;
      }
      const finalScore = deriveScorecardOverrideScore(
        scorecardOverrideRowKey,
        parsedNumeric,
        parsed
      );
      return {
        ...prev,
        [scorecardOverrideRowKey]: {
          score: finalScore,
          reason: scorecardOverrideDraftReason,
          numericValue:
            parsedNumeric != null && Number.isFinite(parsedNumeric)
              ? parsedNumeric
              : null,
        },
      };
    });
    scorecardOverrideDrawerDisclosure.onClose();
  };

  const displayedCashBalance =
    cashBalanceOverride !== null
      ? cashBalanceOverride
      : accountingCashFromBq != null &&
          Number.isFinite(accountingCashFromBq.amount)
        ? accountingCashFromBq.amount
        : fetchResult?.cashBalance?.amount || 0;

  const cashBalanceAsOfDisplay =
    cashBalanceOverride !== null
      ? fetchResult?.cashBalance?.asOfDate ??
        accountingCashFromBq?.asOfDate ??
        '-'
      : accountingCashFromBq?.asOfDate ??
        fetchResult?.cashBalance?.asOfDate ??
        '-';

  const cashBalanceInline = (
    <FormControl minW={0} w={'100%'} display={'flex'} flexDirection={'column'}>
      <HStack justifyContent={'space-between'} alignItems={'center'} mb={2} spacing={2}>
        <FormLabel mb={0} noOfLines={2}>
          Cash balance
        </FormLabel>
        <Button
          leftIcon={<SettingsIcon />}
          size={'xs'}
          variant={'ghost'}
          flexShrink={0}
          onClick={openCashOverrideDrawer}
          isDisabled={isSaved}
        >
          Override
        </Button>
      </HStack>
      <Box
        flex={1}
        minH={'40px'}
        px={3}
        display={'flex'}
        flexDirection={'column'}
        justifyContent={'center'}
        borderWidth={'1px'}
        borderColor={'gray.200'}
        borderRadius={'md'}
        bg={'gray.50'}
      >
        <Text fontSize={'md'} fontWeight={700} lineHeight={'short'}>
          ${formatNumberWithDots(Number(displayedCashBalance || 0))}
        </Text>
        <Text fontSize={'xs'} color={'gray.500'} lineHeight={'short'}>
          As of {cashBalanceAsOfDisplay || '-'}
        </Text>
      </Box>
    </FormControl>
  );

  const roasCellBackgroundColor = (cell) => {
    const meets =
      roasGrossNetView === 'gross'
        ? cell.meetsThresholdGross
        : cell.meetsThresholdNet;
    if (meets === undefined || meets === null) {
      return undefined;
    }
    return meets ? 'green.50' : 'red.50';
  };

  const roasCellValueColor = (cell) => {
    const meets =
      roasGrossNetView === 'gross'
        ? cell.meetsThresholdGross
        : cell.meetsThresholdNet;
    if (meets === undefined || meets === null) {
      return undefined;
    }
    return meets ? 'green.700' : 'red.700';
  };

  /** Tooltip for cells with an evaluated covenant (pass/fail coloring). */
  const roasCovenantTooltipLabel = (cell) => {
    const meets =
      roasGrossNetView === 'gross'
        ? cell.meetsThresholdGross
        : cell.meetsThresholdNet;
    if (
      meets === undefined ||
      meets === null ||
      cell.thresholdPct == null ||
      !Number.isFinite(Number(cell.thresholdPct))
    ) {
      return null;
    }
    const minPct = formatNumberWithDots(Number(cell.thresholdPct) * 100);
    const mob =
      cell.thresholdCovenantMonth != null &&
      Number.isFinite(Number(cell.thresholdCovenantMonth))
        ? Number(cell.thresholdCovenantMonth)
        : null;
    const mobPart =
      mob != null ? `Covenant (MOB ${mob})` : 'Covenant';
    return `${mobPart}: minimum ROAS ${minPct}%`;
  };

  const formatRoasCellValue = (cell) => {
    const raw =
      roasGrossNetView === 'gross' ? cell.grossRoasPct : cell.netRoasPct;
    if (raw === undefined || raw === null || Number.isNaN(Number(raw))) {
      return '—';
    }
    // Same scale as ROAS Targets and deal MinRoasPct: decimal (e.g. 0.26 → 26%).
    return `${formatNumberWithDots(Number(raw) * 100)}%`;
  };

  const orderedResultRows = useMemo(
    () => orderTearsheetExposureResultRows(fetchResult?.resultRows || []),
    [fetchResult?.resultRows]
  );

  const handleTearsheetAssistSend = useCallback(
    async ({ message, history }) => {
      const contextData = buildTearsheetAssistContext({
        customerId,
        fetchResult,
        mode,
        saveName,
        scorecardScoreOverrides,
      });
      const response = await tearsheetAssistApi({
        message,
        history,
        contextData,
      });
      return {
        text: response?.summary || 'Summary complete.',
        usage: response?.usage,
      };
    },
    [customerId, fetchResult, mode, saveName, scorecardScoreOverrides]
  );

  const cashflowMetricsDisplayRows = useMemo(() => {
    const raw = (fetchResult?.cashflowMetrics ?? []).filter(
      (row) => !HIDDEN_CASHFLOW_METRIC_LABELS.has(row.label)
    );
    let annual = computeAnnualRunrateFromCashflowItems(
      cashflowEmbed.results?.items
    );
    if (annual == null || !Number.isFinite(annual)) {
      annual = finiteNumberOrNull(fetchResult?.annualRunRate);
    }
    const showAnnualRunRateLoading =
      Boolean(fetchResult?.cashflowScenarioId) &&
      cashflowEmbed.loading &&
      annual == null;
    const out = [];
    let runwaySeen = false;
    let insertedAnnual = false;
    for (const row of raw) {
      if (row.label === 'Duration' && !insertedAnnual && runwaySeen) {
        if (annual != null && Number.isFinite(annual)) {
          out.push({
            label: CASHFLOW_METRIC_ANNUAL_RUN_RATE_LABEL,
            valueText: String(annual),
            isDerivedAnnualRunRate: true,
          });
          insertedAnnual = true;
        } else if (showAnnualRunRateLoading) {
          out.push({
            label: CASHFLOW_METRIC_ANNUAL_RUN_RATE_LABEL,
            valueText: '',
            isDerivedAnnualRunRate: true,
            isAnnualRunRateLoading: true,
          });
          insertedAnnual = true;
        }
      }
      out.push(row);
      if (row.label === 'Runway Revenue') {
        runwaySeen = true;
      }
    }
    return out;
  }, [
    fetchResult?.cashflowMetrics,
    fetchResult?.cashflowScenarioId,
    fetchResult?.annualRunRate,
    cashflowEmbed.results,
    cashflowEmbed.loading,
  ]);

  const scorecardFinalScoreStats = useMemo(() => {
    const rows = fetchResult?.cashflowScorecard ?? [];
    const finalBands = fetchResult?.scorecardGradeThresholds?.finalScore;
    const scored = rows.filter((r) => {
      const effectiveScore = scorecardScoreOverrides[r.key]?.score ?? r.score;
      return effectiveScore != null && Number.isInteger(Number(effectiveScore));
    });
    if (scored.length === 0) {
      return {
        mean: null,
        bandScore: savedScorecardFinalScore ?? null,
      };
    }
    const avg =
      scored.reduce((sum, r) => {
        const effectiveScore = scorecardScoreOverrides[r.key]?.score ?? r.score;
        return sum + Number(effectiveScore);
      }, 0) / scored.length;
    const minA = finalBands?.minAverageForGradeA ?? 2.5;
    const minB = finalBands?.minAverageForGradeB ?? 1.5;
    if (!Number.isFinite(avg)) {
      return { mean: null, bandScore: null };
    }
    let bandScore = 1;
    if (avg >= minA) bandScore = 3;
    else if (avg >= minB) bandScore = 2;
    return { mean: avg, bandScore };
  }, [
    fetchResult?.cashflowScorecard,
    fetchResult?.scorecardGradeThresholds?.finalScore,
    scorecardScoreOverrides,
    savedScorecardFinalScore,
  ]);

  const scorecardFinalScoreMean = scorecardFinalScoreStats.mean;
  const scorecardFinalScore = scorecardFinalScoreStats.bandScore;

  const roasTargetCovenantSummary = useMemo(
    () =>
      computeRoasTargetCovenantSummary({
        roasTargets: fetchResult?.roasTargets,
        roasCohort: fetchResult?.roasCohort,
      }),
    [fetchResult?.roasTargets, fetchResult?.roasCohort]
  );

  const roasTargetByCohortPeriod = useMemo(
    () => buildRoasTargetValueByCohortPeriod(fetchResult?.roasTargets),
    [fetchResult?.roasTargets]
  );

  const hasRoasTargetByPeriod = (fetchResult?.roasTargets?.length ?? 0) > 0;

  const roasCohortPerformanceTooltip = useMemo(
    () =>
      buildRoasCohortPerformanceTooltip({
        predictionModel: savedConfigState.predictionModel,
        includeRefunds: savedConfigState.includeRefunds,
      }),
    [savedConfigState.predictionModel, savedConfigState.includeRefunds]
  );

  const roasGrossNetPolicy = useMemo(
    () => resolveRoasGrossNetViewPolicy(fetchResult?.roasCohort?.rows),
    [fetchResult?.roasCohort?.rows]
  );

  useEffect(() => {
    if (!roasGrossNetPolicy.toggleEnabled && roasGrossNetPolicy.view) {
      setRoasGrossNetView(roasGrossNetPolicy.view);
    }
  }, [roasGrossNetPolicy]);

  /** First MOB index (0-based) where modeled ROAS ≥ 100%; scorecard shows index + 1 as period. */
  const formatResultValue = (resultRow) => {
    if (resultRow.value === null || resultRow.value === undefined) {
      return '-';
    }

    const isPercentageValue =
      resultRow.kpi?.includes('%') || resultRow.kpi?.includes('MoM');
    const isCurrencyValue =
      resultRow.kpi?.includes('Funding') ||
      resultRow.kpi?.includes('Cash Balance') ||
      resultRow.kpi?.includes('Exposure') ||
      resultRow.kpi?.includes('Repayment');
    const formattedValue = formatNumberWithDots(Number(resultRow.value));

    if (isPercentageValue) {
      return `${formattedValue}%`;
    }
    if (isCurrencyValue) {
      return `$${formattedValue}`;
    }
    return formattedValue;
  };

  const getResultValueTooltipLabel = (resultRow) => {
    if (resultRow.kpi === TEARSHEET_KPI_CURRENT_EXPOSURE) {
      return buildCurrentExposureTooltip();
    }
    if (isPostFundingExposureKpi(resultRow.kpi)) {
      return buildPostFundingExposureTooltip({
        fetchResult,
        formatCurrency: (value) => `$${formatNumberWithDots(value)}`,
      });
    }
    if (resultRow.kpi === TEARSHEET_KPI_MAX_MODELED_EXPOSURE) {
      return buildMaxModeledExposureTooltip();
    }

    const currentMonthSpendAmount = Number(fetchResult?.currentMonthSpend);
    const lastMonthSpendAmount = Number(fetchResult?.lastMonthSpend);
    const twoMonthsAgoSpendAmount = Number(fetchResult?.twoMonthsAgoSpend);

    if (
      resultRow.kpi === 'Max MoM % increase (Current Month)' ||
      resultRow.kpi === 'Max MOM % increase (Current Month)' ||
      resultRow.kpi === 'MoM Growth (Current Month)'
    ) {
      if (!Number.isFinite(currentMonthSpendAmount) || !Number.isFinite(lastMonthSpendAmount)) {
        return null;
      }
      return `${formatNumberWithDots(currentMonthSpendAmount)}/${formatNumberWithDots(lastMonthSpendAmount)}`;
    }

    if (
      resultRow.kpi === 'Max MoM % increase (Last Month)' ||
      resultRow.kpi === 'Max MOM % increase (Last Month)' ||
      resultRow.kpi === 'MoM Growth (Last Month)'
    ) {
      if (!Number.isFinite(lastMonthSpendAmount) || !Number.isFinite(twoMonthsAgoSpendAmount)) {
        return null;
      }
      return `${formatNumberWithDots(lastMonthSpendAmount)}/${formatNumberWithDots(twoMonthsAgoSpendAmount)}`;
    }

    return null;
  };

  const getCheckDisplay = (checkStatus) => {
    if (checkStatus === 'pass') {
      return { label: 'OK', color: 'green.500' };
    }
    if (checkStatus === 'fail') {
      return { label: 'Failed', color: 'red.500' };
    }
    return null;
  };

  const formatConstraintValue = (constraintText) => {
    if (!constraintText) {
      return '-';
    }
    const shouldInterpretAsPercentDecimal = constraintText.includes('%');
    return constraintText.replace(/-?\d+(?:\.\d+)?/g, (numericPart) => {
      let numericValue = Number(numericPart);
      if (
        shouldInterpretAsPercentDecimal &&
        Number.isFinite(numericValue) &&
        Math.abs(numericValue) > 0 &&
        Math.abs(numericValue) < 1
      ) {
        numericValue *= 100;
      }
      return formatNumberWithDots(numericValue);
    });
  };

  const isCreateControlsDisabled = isSaved;

  const columns = [
    columnHelper.display({
      id: 'sequenceNumber',
      cell: (info) => {
        const tearsheet = info.row.original;
        return tearsheet.sequenceNumber || info.row.index + 1;
      },
      header: '#',
      meta: { maxWidth: 20 },
    }),
    columnHelper.accessor('name', {
      cell: (info) => {
        const tearsheet = info.row.original;
        return (
          tearsheet.name ||
          `Tearsheet ${moment(tearsheet.createdAt || tearsheet.date).format('MMM YYYY')}`
        );
      },
      header: 'NAME',
    }),
    columnHelper.accessor('createdAt', {
      cell: (info) => {
        const createdAt = info.getValue();
        if (!createdAt) {
          return '-';
        }
        return moment(createdAt).format('MMM DD, YYYY HH:mm');
      },
      header: 'DATE OF CREATION',
    }),
    columnHelper.accessor('finalScore', {
      cell: (info) => {
        const score = info.getValue();
        if (score == null) {
          return '-';
        }
        const scoreNum = Number(score);
        const color = scorecardScoreColumnColor(scoreNum);
        const gradeLabel =
          scoreNum === 3 ? 'A' : scoreNum === 2 ? 'B' : scoreNum === 1 ? 'C' : null;
        return (
          <Text as={'span'} color={color} fontWeight={'semibold'}>
            {scoreNum}
            {gradeLabel ? ` (${gradeLabel})` : ''}
          </Text>
        );
      },
      header: 'FINAL SCORE',
    }),
    columnHelper.accessor('userId', {
      cell: (info) => {
        const userId = info.getValue();
        return getUserName(userId) || userId;
      },
      header: 'PRODUCED BY',
    }),
    columnHelper.display({
      id: 'actions',
      meta: {
        maxWidth: 10,
        align: 'right',
      },
      cell: (info) => (
        <TwMenu
          items={[
            {
              label: 'Download ZIP',
              isDisabled: pdfGeneratingId != null,
              onClick: (event) => {
                event.stopPropagation();
                const t = info.row.original;
                downloadSavedTearsheetZip(t.id, t);
              },
            },
            {
              label: 'Delete',
              onClick: (event) => {
                event.stopPropagation();
                const t = info.row.original;
                const displayName =
                  t.name ||
                  `Tearsheet ${moment(t.createdAt || t.date).format('MMM YYYY')}`;
                openDeleteTearsheetConfirm(t.id, displayName);
              },
            },
          ]}
        />
      ),
    }),
  ];

  if (isLoading && !showCreateMode) {
    return (
      <AbsoluteCenter>
        <Spinner />
      </AbsoluteCenter>
    );
  }

  if (!showCreateMode) {
    return (
      <Box maxW={'container.xl'}>
        <Card>
          <Flex justifyContent={'space-between'} alignItems={'center'} mb={8}>
            <Heading variant={'sectionHeader'} borderColor={'twelve.green.300'}>
              Tearsheets
            </Heading>
            <Button variant={'brand'} onClick={enterCreateMode}>
              + New Tearsheet
            </Button>
          </Flex>
          <Box mx={-5}>
            <DataTable
              data={tearsheets}
              columns={columns}
              rowHeight={'100px'}
              onRowClick={(row) => {
                navigate(
                  `/customers/${customerId}/performance/tearsheet_new/${row.id}`
                );
              }}
            />
          </Box>
        </Card>

        <Modal
          isOpen={deleteConfirmModalDisclosure.isOpen}
          onClose={closeDeleteTearsheetConfirm}
          isCentered
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Delete tearsheet?</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text>
                Are you sure you want to delete{' '}
                <Text as={'span'} fontWeight={600}>
                  {pendingDeleteTearsheet.displayName}
                </Text>
                ? This cannot be undone.
              </Text>
            </ModalBody>
            <ModalFooter>
              <Button mr={3} variant={'outline'} onClick={closeDeleteTearsheetConfirm}>
                Cancel
              </Button>
              <Button
                colorScheme={'red'}
                onClick={confirmDeleteTearsheet}
                isLoading={isLoading}
              >
                Delete
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    );
  }

  return (
    <Box maxW={'container.xl'}>
      <Card>
        <Flex justifyContent={'space-between'} alignItems={'center'} mb={6}>
          <Heading variant={'sectionHeader'} borderColor={'twelve.green.300'}>
            New Tearsheet
          </Heading>
          <HStack>
            <Button
              leftIcon={<SettingsIcon />}
              variant={'outline'}
              onClick={onOpenConfiguration}
              isDisabled={isCreateControlsDisabled}
            >
              Configuration
            </Button>
            <Button
              variant={'outline'}
              onClick={() => {
                if (savedTearsheetId) {
                  navigate(
                    `/customers/${customerId}/performance/tearsheet_new`
                  );
                } else {
                  setShowCreateMode(false);
                }
              }}
            >
              Back
            </Button>
            <Button
              variant={'brand'}
              onClick={onOpenSaveModal}
              isDisabled={!fetchResult?.resultRows?.length || isSaved}
              isLoading={isSaving}
            >
              Save Tearsheet
            </Button>
          </HStack>
        </Flex>

        <Card variant={'outline'}>
          <HStack justifyContent={'space-between'} mb={5}>
            <Heading size={'sm'}>Deal & Input Parameters</Heading>
            <RadioGroup
              value={mode}
              onChange={(value) => {
                if (isCreateControlsDisabled) {
                  return;
                }
                setMode(value);
                if (value === 'installment' && createFormOptions.installments.length === 0) {
                  fetchTearsheetNewApi({
                    customerId,
                    data: {
                      mode: 'installment',
                      dealId: null,
                      installmentId: null,
                      requestedFundingPct: null,
                      marketingSpendAmount: null,
                      cashBalanceOverride: null,
                    },
                  }).then((res) => {
                    setCreateFormOptions((prev) => ({
                      ...prev,
                      installments: res.installments || [],
                    }));
                  }).catch(() => {});
                }
              }}
            >
              <HStack spacing={3}>
                <Radio value={'deal'}>From Deal</Radio>
                <Radio value={'installment'}>From Installment</Radio>
              </HStack>
            </RadioGroup>
          </HStack>

          {mode === 'deal' ? (
            <SimpleGrid
              columns={{ base: 1, md: 4 }}
              spacing={4}
              mb={4}
              w={'100%'}
              alignItems={'flex-end'}
            >
              <FormControl minW={0}>
                <FormLabel>Deal</FormLabel>
                <SelectInput
                  placeholder={'Select Deal'}
                  options={dealsOptions}
                  value={selectedDealId}
                  onChange={setSelectedDealId}
                  isDisabled={isCreateControlsDisabled}
                />
              </FormControl>
              <FormControl minW={0}>
                <FormLabel>Marketing Spend</FormLabel>
                <Input
                  value={marketingSpendAmount}
                  onChange={(event) =>
                    setMarketingSpendAmount(
                      normalizeNumericInputValue(event.target.value)
                    )
                  }
                  placeholder={'Enter marketing spend'}
                  isDisabled={isCreateControlsDisabled}
                />
              </FormControl>
              <FormControl minW={0}>
                <FormLabel>Requested Funding %</FormLabel>
                <Input
                  value={requestedFundingPct}
                  onChange={(event) =>
                    setRequestedFundingPct(
                      normalizeNumericInputValue(event.target.value)
                    )
                  }
                  placeholder={'Enter funding %'}
                  isDisabled={isCreateControlsDisabled}
                />
              </FormControl>
              {cashBalanceInline}
            </SimpleGrid>
          ) : (
            <SimpleGrid
              columns={{ base: 1, md: 2 }}
              spacing={4}
              mb={4}
              w={'100%'}
              alignItems={'flex-end'}
            >
              <FormControl minW={0}>
                <FormLabel>Installment</FormLabel>
                <SelectInput
                  placeholder={'Select Installment'}
                  options={installmentsOptions}
                  value={selectedInstallmentId}
                  onChange={setSelectedInstallmentId}
                  isDisabled={isCreateControlsDisabled}
                />
              </FormControl>
              {cashBalanceInline}
            </SimpleGrid>
          )}

          <Button
            variant={'brand'}
            onClick={runFetch}
            isLoading={isFetching}
            isDisabled={isCreateControlsDisabled}
          >
            Fetch
          </Button>
        </Card>

        {scorecardFinalScore != null && (() => {
          const rawThresholds = fetchResult?.scorecardGradeThresholds;
          const GRADE_THRESHOLDS = {
            runRate: {
              3: rawThresholds?.exposureToRunRate?.grade3 ?? 40,
              2: rawThresholds?.exposureToRunRate?.grade2 ?? 20,
              1: rawThresholds?.exposureToRunRate?.grade1 ?? 10,
            },
            runOff: {
              3: rawThresholds?.exposureToRunOff?.grade3 ?? 80,
              2: rawThresholds?.exposureToRunOff?.grade2 ?? 50,
              1: rawThresholds?.exposureToRunOff?.grade1 ?? 25,
            },
          };
          const gradeLabel =
            scorecardFinalScore === 3 ? 'A' : scorecardFinalScore === 2 ? 'B' : 'C';
          const gradeColorScheme =
            scorecardFinalScore === 3 ? 'green' : scorecardFinalScore === 2 ? 'yellow' : 'red';
          let annualRunrate = computeAnnualRunrateFromCashflowItems(
            cashflowEmbed.results?.items
          );
          if (annualRunrate == null || !Number.isFinite(annualRunrate)) {
            annualRunrate = finiteNumberOrNull(fetchResult?.annualRunRate);
          }
          const futureExposureForRunRate = fetchResult?.futureExposure ?? null;
          const actualRunRateLive =
            annualRunrate != null &&
            futureExposureForRunRate != null &&
            annualRunrate !== 0
              ? Math.round(
                  (futureExposureForRunRate / annualRunrate) * 100 * 100
                ) / 100
              : null;
          const actualRunRateFromSaved = finiteNumberOrNull(
            fetchResult?.exposureToRunRatePct
          );
          const actualRunRate = actualRunRateLive ?? actualRunRateFromSaved;
          const exposureToRunRatePending =
            Boolean(fetchResult?.cashflowScenarioId) &&
            cashflowEmbed.loading &&
            actualRunRate == null;
          const runoffRevenueMetric = fetchResult?.cashflowMetrics?.find(
            (m) => m.label === 'Runoff Revenue (Calculated with Cash)'
          );
          const runoffRevenueParsed = runoffRevenueMetric?.valueText
            ? parseFloat(runoffRevenueMetric.valueText)
            : null;
          const runoffRevenue =
            runoffRevenueParsed != null && Number.isFinite(runoffRevenueParsed)
              ? runoffRevenueParsed
              : null;
          const futureExposureForRunOff = fetchResult?.futureExposure ?? null;
          const actualRunOff =
            runoffRevenue != null &&
            futureExposureForRunOff != null &&

            runoffRevenue !== 0
              ? Math.round(
                  (futureExposureForRunOff / runoffRevenue) * 100 * 100
                ) / 100
              : null;
          const kpiColor = (actual, threshold) => {
            if (actual == null) return 'inherit';
            return actual <= threshold ? 'green.600' : 'red.600';
          };
          const runRateThresholdPct = GRADE_THRESHOLDS.runRate[scorecardFinalScore];
          const runOffThresholdPct = GRADE_THRESHOLDS.runOff[scorecardFinalScore];
          const runRateCheckOk =
            actualRunRate != null && actualRunRate <= runRateThresholdPct;
          const runOffCheckOk =
            actualRunOff != null && actualRunOff <= runOffThresholdPct;
          return (
            <Card mt={6} variant={'outline'}>
              <HStack spacing={4} alignItems={'center'} mb={4} flexWrap={'wrap'}>
                <Heading size={'sm'}>Final Score</Heading>
                <Tooltip
                  label={
                    scorecardFinalScoreMean != null
                      ? 'Mean of scorecard row scores (each 1–3). The letter grade follows your configured min-average thresholds for A/B/C.'
                      : ''
                  }
                  hasArrow
                  isDisabled={scorecardFinalScoreMean == null}
                >
                  <Text
                    fontSize={'2xl'}
                    fontWeight={'bold'}
                    color={scorecardScoreColumnColor(scorecardFinalScore)}
                    cursor={scorecardFinalScoreMean != null ? 'help' : 'default'}
                  >
                    {scorecardFinalScoreMean != null
                      ? scorecardFinalScoreMean.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : scorecardFinalScore != null
                        ? String(scorecardFinalScore)
                        : '—'}
                  </Text>
                </Tooltip>
                <Badge colorScheme={gradeColorScheme}>Grade {gradeLabel}</Badge>
                {scorecardFinalScoreMean != null ? (
                  <Text fontSize={'xs'} color={'gray.500'}>
                    mean · scale 1–3
                  </Text>
                ) : null}
              </HStack>
              <Table size={'sm'}>
                <Thead>
                  <Tr>
                    <Th>KPI</Th>
                    <Th>Threshold</Th>
                    <Th>Actual</Th>
                    <Th>Check</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td>
                      <HStack spacing={1} alignItems={'center'}>
                        <Text as={'span'}>Exposure to Run Rate</Text>
                        <Tooltip label={EXPOSURE_TO_RUN_RATE_TOOLTIP} hasArrow placement={'top'}>
                          <InfoOutlineIcon color={'gray.500'} boxSize={4} cursor={'help'} />
                        </Tooltip>
                      </HStack>
                    </Td>
                    <Td>Up to {runRateThresholdPct}%</Td>
                    <Td
                      color={
                        exposureToRunRatePending
                          ? 'inherit'
                          : kpiColor(actualRunRate, runRateThresholdPct)
                      }
                    >
                      {exposureToRunRatePending ? (
                        <HStack spacing={2} justifyContent={'flex-start'}>
                          <Spinner size={'sm'} color={'gray.400'} />
                          <Text fontSize={'sm'} color={'gray.500'}>
                            Loading…
                          </Text>
                        </HStack>
                      ) : actualRunRate != null ? (
                        `${actualRunRate}%`
                      ) : (
                        '—'
                      )}
                    </Td>
                    <Td
                      color={
                        exposureToRunRatePending || actualRunRate == null
                          ? 'inherit'
                          : runRateCheckOk
                            ? 'green.500'
                            : 'red.500'
                      }
                    >
                      {exposureToRunRatePending ? (
                        <HStack spacing={2}>
                          <Spinner size={'sm'} color={'gray.400'} />
                          <Text fontSize={'sm'} color={'gray.500'}>
                            …
                          </Text>
                        </HStack>
                      ) : actualRunRate == null ? (
                        '—'
                      ) : runRateCheckOk ? (
                        'OK'
                      ) : (
                        'Failed'
                      )}
                    </Td>
                  </Tr>
                  <Tr>
                    <Td>Exposure to Runoff (With Cash)</Td>
                    <Td>Up to {runOffThresholdPct}%</Td>
                    <Td
                      color={kpiColor(actualRunOff, runOffThresholdPct)}
                    >
                      {actualRunOff != null ? `${actualRunOff}%` : '—'}
                    </Td>
                    <Td
                      color={
                        actualRunOff == null
                          ? 'inherit'
                          : runOffCheckOk
                            ? 'green.500'
                            : 'red.500'
                      }
                    >
                      {actualRunOff == null ? '—' : runOffCheckOk ? 'OK' : 'Failed'}
                    </Td>
                  </Tr>
                </Tbody>
              </Table>
            </Card>
          );
        })()}

        {roasTargetCovenantSummary ? (
          <Alert
            mt={6}
            status={roasCovenantOverallAlertStatus(roasTargetCovenantSummary)}
            variant={'left-accent'}
          >
            <AlertIcon />
            <AlertDescription fontSize={'sm'}>
              <Text as={'span'} fontWeight={'semibold'}>
                ROAS Target by Period covenant:{' '}
              </Text>
              <Text as={'span'}>
                Gross —{' '}
                <Text
                  as={'span'}
                  fontWeight={'semibold'}
                  color={roasCovenantBasisTextColor(roasTargetCovenantSummary.gross)}
                >
                  {formatRoasCovenantBasisStatus(
                    roasTargetCovenantSummary.gross,
                    roasTargetCovenantSummary.failedGrossMobs
                  )}
                </Text>
                . Net —{' '}
                <Text
                  as={'span'}
                  fontWeight={'semibold'}
                  color={roasCovenantBasisTextColor(roasTargetCovenantSummary.net)}
                >
                  {formatRoasCovenantBasisStatus(
                    roasTargetCovenantSummary.net,
                    roasTargetCovenantSummary.failedNetMobs
                  )}
                </Text>
                .
              </Text>
            </AlertDescription>
          </Alert>
        ) : null}

        {fetchResult?.cashflowScorecard?.length > 0 ? (
          <Card mt={6} variant={'outline'}>
            <HStack mb={4} alignItems={'center'} spacing={3}>
              <Heading size={'sm'}>Scorecard</Heading>
              {fetchResult?.scorecardConfigVersion ? (
                <Badge colorScheme={'purple'}>
                  v{fetchResult.scorecardConfigVersion}
                </Badge>
              ) : null}
            </HStack>
            <Table size={'sm'}>
              <Thead>
                <Tr>
                  <Th>Name</Th>
                  <Th isNumeric>Value</Th>
                  <Th isNumeric>Score (1-3)</Th>
                </Tr>
              </Thead>
              <Tbody>
                {fetchResult.cashflowScorecard.map((row, index) => (
                  <Tr
                    key={row.name}
                    borderBottomWidth={
                      index < fetchResult.cashflowScorecard.length - 1
                        ? '1px'
                        : '0px'
                    }
                    borderColor={'gray.200'}
                  >
                    <Td
                      borderBottomWidth={
                        index < fetchResult.cashflowScorecard.length - 1
                          ? '1px'
                          : '0px'
                      }
                      borderColor={'gray.200'}
                    >
                      {(() => {
                        const tip = getScorecardNameTooltip(row.name);
                        if (!tip) {
                          return row.name;
                        }
                        return (
                          <Tooltip label={tip} hasArrow placement={'top'}>
                            <HStack
                              as={'span'}
                              spacing={1}
                              cursor={'help'}
                              w={'fit-content'}
                            >
                              <InfoOutlineIcon boxSize={3.5} color={'gray.500'} />
                              <Text as={'span'}>{row.name}</Text>
                            </HStack>
                          </Tooltip>
                        );
                      })()}
                    </Td>
                    <Td
                      isNumeric
                      borderBottomWidth={
                        index < fetchResult.cashflowScorecard.length - 1
                          ? '1px'
                          : '0px'
                      }
                      borderColor={'gray.200'}
                    >
                      {isUaPredictionScorecardRow(row)
                        ? formatCacPredictionRatioPercent(
                            fetchResult?.cacPrediction,
                            row
                          )
                        : formatScorecardValueCell(row)}
                    </Td>
                    <Td
                      isNumeric
                      borderBottomWidth={
                        index < fetchResult.cashflowScorecard.length - 1
                          ? '1px'
                          : '0px'
                      }
                      borderColor={'gray.200'}
                    >
                      {(() => {
                        const bandTip =
                          (row.bands || []).length > 0
                            ? [...row.bands]
                                .sort((a, b) => b.score - a.score)
                                .map((b) => `${b.score}: ${b.label}`)
                                .join('\n')
                            : '';
                        const scoreOverride = scorecardScoreOverrides[row.key];
                        const effectiveScore =
                          scoreOverride?.score ?? row.score;
                        const scoreDisplay =
                          effectiveScore != null && effectiveScore !== ''
                            ? String(effectiveScore)
                            : '—';
                        const scoreNum = Number(effectiveScore);
                        const scoreColor =
                          Number.isInteger(scoreNum) &&
                          scoreNum >= 1 &&
                          scoreNum <= 3
                            ? scorecardScoreColumnColor(scoreNum)
                            : 'gray.400';
                        return (
                          <HStack spacing={1} justifyContent={'flex-end'}>
                            <Tooltip
                              isDisabled={!bandTip}
                              label={bandTip}
                              hasArrow
                              placement={'left'}
                              whiteSpace={'pre-line'}
                            >
                              <Text
                                as={'span'}
                                cursor={bandTip ? 'help' : 'default'}
                                color={scoreColor}
                                fontWeight={'semibold'}
                              >
                                {scoreDisplay}
                                {scoreOverride != null || row.overrideActive
                                  ? ' *'
                                  : ''}
                              </Text>
                            </Tooltip>
                            <IconButton
                              icon={<EditIcon />}
                              size={'xs'}
                              variant={'ghost'}
                              aria-label={
                                isSaved && row.overrideActive
                                  ? 'View score override'
                                  : 'Override score'
                              }
                              isDisabled={isSaved && !row.overrideActive}
                              onClick={() => openScorecardOverrideDrawer(row)}
                            />
                          </HStack>
                        );
                      })()}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Card>
        ) : null}

        {fetchResult?.cacPrediction ? (
          <Card mt={6} variant={'outline'}>
            <HStack
              spacing={1}
              mb={uaPredictionOpen ? 4 : 0}
              cursor={'pointer'}
              userSelect={'none'}
              w={'fit-content'}
              onClick={() => setUaPredictionOpen((open) => !open)}
            >
              {uaPredictionOpen ? (
                <ChevronDownIcon boxSize={4} color={'gray.500'} />
              ) : (
                <ChevronRightIcon boxSize={4} color={'gray.500'} />
              )}
              <Heading size={'sm'}>UA Prediction</Heading>
              <Tooltip
                label={SCORECARD_TOOLTIP_UA_PREDICTION}
                hasArrow
                placement={'top'}
              >
                <InfoOutlineIcon
                  ml={1}
                  color={'gray.500'}
                  boxSize={4}
                  cursor={'help'}
                  onClick={(event) => event.stopPropagation()}
                />
              </Tooltip>
            </HStack>
            <Collapse in={uaPredictionOpen} animateOpacity>
              <CacPredictionRegressionDetailPanel
                detail={fetchResult.cacPrediction}
              />
            </Collapse>
          </Card>
        ) : null}

        {!!fetchResult?.resultRows?.length && (
          <Card mt={6} variant={'outline'}>
            <CollapsibleSectionHeader
              title={'Covenants'}
              isOpen={covenantsOpen}
              onToggle={() => setCovenantsOpen((open) => !open)}
            />
            <Collapse in={covenantsOpen} animateOpacity>
              <Table size={'sm'}>
              <Thead>
                <Tr>
                  <Th>Section</Th>
                  <Th>KPI</Th>
                  <Th>Value</Th>
                  <Th>Constraint</Th>
                  <Th>Date</Th>
                  <Th>Check</Th>
                </Tr>
              </Thead>
              <Tbody>
                {orderedResultRows.map((row, index) => (
                  <Tr
                    key={index}
                    borderBottomWidth={
                      index < orderedResultRows.length - 1 ? '1px' : '0px'
                    }
                    borderColor={'gray.200'}
                  >
                    <Td
                      borderBottomWidth={index < orderedResultRows.length - 1 ? '1px' : '0px'}
                      borderColor={'gray.200'}
                    >
                      {row.area}
                    </Td>
                    <Td
                      borderBottomWidth={index < orderedResultRows.length - 1 ? '1px' : '0px'}
                      borderColor={'gray.200'}
                    >
                      {displayTearsheetCovenantKpi(row.kpi)}
                    </Td>
                    <Td
                      borderBottomWidth={index < orderedResultRows.length - 1 ? '1px' : '0px'}
                      borderColor={'gray.200'}
                    >
                      {(() => {
                        const formattedResultValue = formatResultValue(row);
                        const resultValueTooltipLabel = getResultValueTooltipLabel(row);
                        const shouldShowResultValueTooltip =
                          resultValueTooltipLabel && formattedResultValue !== '-';
                        if (!shouldShowResultValueTooltip) {
                          return formattedResultValue;
                        }
                        return (
                          <Tooltip
                            label={resultValueTooltipLabel}
                            hasArrow
                            placement={'top'}
                          >
                            <Text as={'span'} cursor={'help'} fontWeight={700}>
                              {formattedResultValue}
                            </Text>
                          </Tooltip>
                        );
                      })()}
                    </Td>
                    <Td
                      borderBottomWidth={index < orderedResultRows.length - 1 ? '1px' : '0px'}
                      borderColor={'gray.200'}
                    >
                      {formatConstraintValue(row.constraint)}
                    </Td>
                    <Td
                      borderBottomWidth={index < orderedResultRows.length - 1 ? '1px' : '0px'}
                      borderColor={'gray.200'}
                    >
                      {row.date || '-'}
                    </Td>
                    <Td
                      borderBottomWidth={index < orderedResultRows.length - 1 ? '1px' : '0px'}
                      borderColor={'gray.200'}
                    >
                      {(() => {
                        const checkDisplay = getCheckDisplay(row.check);
                        if (!checkDisplay) {
                          return '-';
                        }
                        return (
                          <Text color={checkDisplay.color} fontWeight={700}>
                            {checkDisplay.label}
                          </Text>
                        );
                      })()}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            </Collapse>
          </Card>
        )}

        {fetchResult?.roasCohort?.rows?.length > 0 && (
          <Card mt={6} variant={'outline'}>
            <CollapsibleSectionHeader
              title={'ROAS Cohort Performance'}
              isOpen={roasCohortOpen}
              onToggle={() => setRoasCohortOpen((open) => !open)}
              infoTooltip={roasCohortPerformanceTooltip}
              trailing={
                roasGrossNetPolicy.toggleEnabled ? (
                  <RadioGroup
                    value={roasGrossNetView}
                    onChange={setRoasGrossNetView}
                  >
                    <HStack spacing={4}>
                      <Radio value={'gross'}>Gross</Radio>
                      <Radio value={'net'}>Net</Radio>
                    </HStack>
                  </RadioGroup>
                ) : (
                  <Text
                    fontSize={'sm'}
                    fontWeight={'semibold'}
                    textTransform={'capitalize'}
                    color={'gray.700'}
                  >
                    {roasGrossNetPolicy.view || roasGrossNetView}
                  </Text>
                )
              }
            />
            <Collapse in={roasCohortOpen} animateOpacity>
            <Box overflowX={'auto'} borderWidth={'1px'} borderColor={'gray.200'} borderRadius={'md'}>
              <Table size={'sm'} variant={'simple'} sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                <Thead>
                  <Tr>
                    <Th
                      position={'sticky'}
                      left={0}
                      zIndex={3}
                      bg={'gray.100'}
                      borderBottomWidth={'2px'}
                      borderRightWidth={'1px'}
                      borderColor={'gray.300'}
                      minW={'168px'}
                      boxShadow={'4px 0 8px rgba(0,0,0,0.06)'}
                      verticalAlign={'bottom'}
                    >
                      Cohort
                    </Th>
                    {(fetchResult.roasCohort.periodLabels || []).map((period) => (
                      <Th
                        key={period}
                        bg={'gray.100'}
                        borderBottomWidth={'2px'}
                        borderLeftWidth={'1px'}
                        borderColor={'gray.300'}
                        textAlign={'center'}
                        minW={'76px'}
                        px={2}
                        py={3}
                        whiteSpace={'nowrap'}
                      >
                        P{period}
                      </Th>
                    ))}
                  </Tr>
                  {hasRoasTargetByPeriod ? (
                    <Tr>
                      <Th
                        position={'sticky'}
                        left={0}
                        zIndex={3}
                        bg={'blue.50'}
                        borderBottomWidth={'2px'}
                        borderRightWidth={'1px'}
                        borderColor={'gray.300'}
                        minW={'168px'}
                        boxShadow={'4px 0 8px rgba(0,0,0,0.06)'}
                        fontSize={'xs'}
                        color={'gray.600'}
                        verticalAlign={'middle'}
                      >
                        Min ROAS target
                      </Th>
                      {(fetchResult.roasCohort.periodLabels || []).map((period) => {
                        const targetValue = roasTargetByCohortPeriod.get(period);
                        return (
                          <Th
                            key={`target-${period}`}
                            bg={'blue.50'}
                            borderBottomWidth={'2px'}
                            borderLeftWidth={'1px'}
                            borderColor={'gray.300'}
                            textAlign={'center'}
                            minW={'76px'}
                            px={2}
                            py={2}
                            fontSize={'xs'}
                            color={targetValue != null ? 'blue.700' : 'gray.400'}
                            fontWeight={'semibold'}
                          >
                            {targetValue != null
                              ? `≥${formatNumberWithDots(Number(targetValue) * 100)}%`
                              : '—'}
                          </Th>
                        );
                      })}
                    </Tr>
                  ) : null}
                </Thead>
                <Tbody>
                  {fetchResult.roasCohort.rows.map((row, rowIdx) => {
                    const stripeBg = rowIdx % 2 === 0 ? 'white' : 'gray.50';
                    const rowKeyStable =
                      row.rowGroupId != null && row.rowGroupId !== ''
                        ? row.rowGroupId
                        : `${row.cohortLabel}-${rowIdx}`;
                    return (
                      <Tr key={rowKeyStable}>
                        <Td
                          verticalAlign={'top'}
                          position={'sticky'}
                          left={0}
                          zIndex={2}
                          bg={stripeBg}
                          borderRightWidth={'1px'}
                          borderBottomWidth={'1px'}
                          borderColor={'gray.200'}
                          boxShadow={'4px 0 8px rgba(0,0,0,0.04)'}
                          minW={'168px'}
                        >
                          <Text fontWeight={700}>{row.cohortLabel}</Text>
                          {row.status ? (
                            <Text fontSize={'xs'} color={'gray.500'}>
                              {row.status}
                            </Text>
                          ) : null}
                          {row.subtitle ? (
                            <Text fontSize={'xs'} color={'gray.600'}>
                              {row.subtitle}
                            </Text>
                          ) : null}
                        </Td>
                        {row.cells.map((cell) => {
                          const thresholdBg = roasCellBackgroundColor(cell);
                          const cellBg = thresholdBg ?? stripeBg;
                          const valueColor = roasCellValueColor(cell);
                          const covenantTip = roasCovenantTooltipLabel(cell);
                          const valueText = (
                            <Text
                              fontWeight={500}
                              fontSize={'sm'}
                              color={valueColor}
                            >
                              {formatRoasCellValue(cell)}
                            </Text>
                          );
                          return (
                            <Td
                              key={String(cell.period)}
                              bg={cellBg}
                              borderLeftWidth={'1px'}
                              borderBottomWidth={'1px'}
                              borderColor={'gray.200'}
                              textAlign={'center'}
                              verticalAlign={'middle'}
                              minW={'76px'}
                              px={2}
                              py={2}
                            >
                              <HStack spacing={1} justifyContent={'center'}>
                                {cell.isPredicted ? (
                                  <Tooltip label={'Predicted'} hasArrow>
                                    <StarIcon color={'orange.400'} boxSize={3} />
                                  </Tooltip>
                                ) : null}
                                {covenantTip ? (
                                  <Tooltip label={covenantTip} hasArrow placement={'top'}>
                                    {valueText}
                                  </Tooltip>
                                ) : (
                                  valueText
                                )}
                              </HStack>
                            </Td>
                          );
                        })}
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
            </Collapse>
          </Card>
        )}

        {(
          cashflowMetricsDisplayRows.length > 0 ||
          fetchResult?.cashflowRoasVector?.length > 0 ||
          fetchResult?.cashflowScenarioId ||
          (fetchResult?.cashflowRunoffDebugWithCash?.steps?.length ?? 0) > 0 ||
          (fetchResult?.cashflowRunoffDebugWithoutCash?.steps?.length ?? 0) > 0 ||
          (fetchResult?.cashflowRunwayDebug?.steps?.length ?? 0) > 0
        ) && (
          <Card mt={6} variant={'outline'}>
            <CollapsibleSectionHeader
              title={'Cashflow model'}
              isOpen={cashflowModelOpen}
              onToggle={() => setCashflowModelOpen((open) => !open)}
            />
            <Collapse in={cashflowModelOpen} animateOpacity>

            {cashflowMetricsDisplayRows.length > 0 ? (
              <Box mb={6}>
                <Heading size={'xs'} mb={2} textTransform={'uppercase'} color={'gray.500'}>
                  Metrics
                </Heading>
                <Table size={'sm'}>
                  <Thead>
                    <Tr>
                      <Th>KPI</Th>
                      <Th isNumeric>Value</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {cashflowMetricsDisplayRows.map((row, index) => (
                      <Tr key={`${row.label}-${index}`}>
                        <Td>
                          {row.isDerivedAnnualRunRate ? (
                            <Tooltip
                              label={CASHFLOW_METRIC_ANNUAL_RUN_RATE_TOOLTIP}
                              hasArrow
                              placement={'top'}
                            >
                              <HStack
                                as={'span'}
                                spacing={1}
                                cursor={'help'}
                                w={'fit-content'}
                              >
                                <InfoOutlineIcon boxSize={3.5} color={'gray.500'} />
                                <Text as={'span'}>{row.label}</Text>
                              </HStack>
                            </Tooltip>
                          ) : (
                            row.label
                          )}
                        </Td>
                        <Td isNumeric>
                          {row.isAnnualRunRateLoading ? (
                            <Tooltip
                              label={
                                'Loading detailed cashflow results to compute Annual Run Rate (MOB −1…−3 Total Revenue).'
                              }
                              hasArrow
                              placement={'top'}
                            >
                              <HStack
                                spacing={2}
                                justifyContent={'flex-end'}
                              >
                                <Spinner size={'sm'} color={'gray.400'} />
                                <Text fontSize={'sm'} color={'gray.500'}>
                                  Loading…
                                </Text>
                              </HStack>
                            </Tooltip>
                          ) : (
                            formatCashflowNumericDisplayText(row.valueText, {
                              currency: cashflowMetricLabelIsCurrency(row.label),
                            })
                          )}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            ) : null}

            {fetchResult?.cashflowRoasVector?.length > 0 ? (
              <Box mb={6}>
                <HStack spacing={1} alignItems={'center'} mb={2}>
                  <Heading
                    size={'xs'}
                    mb={0}
                    textTransform={'uppercase'}
                    color={'gray.500'}
                  >
                    Generic cohort ROAS vector
                  </Heading>
                  <Tooltip label={CASHFLOW_ROAS_VECTOR_GROSS_TOOLTIP} hasArrow placement={'top'}>
                    <InfoOutlineIcon
                      color={'gray.500'}
                      boxSize={3.5}
                      cursor={'help'}
                      aria-label={CASHFLOW_ROAS_VECTOR_GROSS_TOOLTIP}
                    />
                  </Tooltip>
                </HStack>
                <Box overflowX={'auto'} borderWidth={'1px'} borderColor={'gray.200'} borderRadius={'md'}>
                  <Table size={'sm'} variant={'simple'} sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                    <Thead>
                      <Tr>
                        <Th
                          position={'sticky'}
                          left={0}
                          zIndex={3}
                          bg={'gray.100'}
                          borderBottomWidth={'2px'}
                          borderRightWidth={'1px'}
                          borderColor={'gray.300'}
                          minW={'168px'}
                          boxShadow={'4px 0 8px rgba(0,0,0,0.06)'}
                          verticalAlign={'bottom'}
                        >
                          MOB
                        </Th>
                        {fetchResult.cashflowRoasVector.map((pt) => (
                          <Th
                            key={`rv-h-${String(pt.mobIndex)}`}
                            bg={'gray.100'}
                            borderBottomWidth={'2px'}
                            borderLeftWidth={'1px'}
                            borderColor={'gray.300'}
                            textAlign={'center'}
                            minW={'76px'}
                            px={2}
                            py={3}
                            whiteSpace={'nowrap'}
                          >
                            M{pt.mobIndex}
                          </Th>
                        ))}
                      </Tr>
                    </Thead>
                    <Tbody>
                      <Tr>
                        <Td
                          verticalAlign={'middle'}
                          position={'sticky'}
                          left={0}
                          zIndex={2}
                          bg={'white'}
                          borderRightWidth={'1px'}
                          borderBottomWidth={'1px'}
                          borderColor={'gray.200'}
                          boxShadow={'4px 0 8px rgba(0,0,0,0.04)'}
                          minW={'168px'}
                        >
                          <Text fontWeight={700}>ROAS</Text>
                        </Td>
                        {fetchResult.cashflowRoasVector.map((pt) => (
                          <Td
                            key={`rv-${String(pt.mobIndex)}`}
                            bg={'white'}
                            borderLeftWidth={'1px'}
                            borderBottomWidth={'1px'}
                            borderColor={'gray.200'}
                            textAlign={'center'}
                            verticalAlign={'middle'}
                            minW={'76px'}
                            px={2}
                            py={2}
                          >
                            <HStack spacing={1} justifyContent={'center'}>
                              {pt.isPredicted ? (
                                <Tooltip label={'Predicted'} hasArrow>
                                  <StarIcon color={'orange.400'} boxSize={3} />
                                </Tooltip>
                              ) : null}
                              <Text fontWeight={500} fontSize={'sm'}>
                                {pt.roasDisplay}
                              </Text>
                            </HStack>
                          </Td>
                        ))}
                      </Tr>
                    </Tbody>
                  </Table>
                </Box>
              </Box>
            ) : null}

            {(fetchResult?.cashflowRunoffDebugWithCash?.steps?.length > 0 ||
              fetchResult?.cashflowRunoffDebugWithoutCash?.steps?.length > 0 ||
              fetchResult?.cashflowRunwayDebug?.steps?.length > 0) ? (
              <Box mb={6}>
                <Button
                  size={'sm'}
                  variant={'outline'}
                  mb={2}
                  onClick={() => setRunoffDebugOpen((open) => !open)}
                >
                  {runoffDebugOpen
                    ? 'Hide runoff/runway calculation steps'
                    : 'Show runoff/runway calculation steps'}
                </Button>
                <Collapse in={runoffDebugOpen} animateOpacity>
                  <Box overflowX={'auto'}>
                    {fetchResult?.cashflowRunoffDebugWithCash?.steps?.length >
                    0 ? (
                      <Box mb={4}>
                        <Text fontWeight={600} mb={2}>
                          With starting cash
                        </Text>
                        <Text fontSize={'xs'} color={'gray.600'} mb={2}>
                          Starting cash:{' '}
                          {formatRunoffDebugCurrency(
                            fetchResult.cashflowRunoffDebugWithCash
                              .startingCash,
                          )}
                          {' · '}
                          Final month:{' '}
                          {formatRunoffDebugNumber(
                            fetchResult.cashflowRunoffDebugWithCash
                              .finalMonthIndex,
                          )}
                          {' · '}
                          Sales at cutoff:{' '}
                          {formatRunoffDebugCurrency(
                            fetchResult.cashflowRunoffDebugWithCash
                              .salesAtCutoff,
                          )}
                          {fetchResult.cashflowRunoffDebugWithCash
                            .requiresMoreData
                            ? ' *'
                            : ''}
                        </Text>
                        <Table size={'sm'} variant={'simple'} sx={{ minWidth: 'max-content' }}>
                          <Thead>
                            <Tr>
                              <Th>Period</Th>
                              <Th isNumeric>Cum opex</Th>
                              <Th isNumeric>Cum repay (tape)</Th>
                              <Th isNumeric>Cum rev (existing)</Th>
                              <Th isNumeric>Numerator</Th>
                              <Th isNumeric>Denominator</Th>
                              <Th isNumeric>Ratio</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {fetchResult.cashflowRunoffDebugWithCash.steps.map(
                              (step, idx) => (
                                <Tr key={`rw-${String(idx)}`}>
                                  <Td>{step.period}</Td>
                                  <Td isNumeric>
                                    {formatRunoffDebugCurrency(
                                      step.cumulativeOpex,
                                    )}
                                  </Td>
                                  <Td isNumeric>
                                    {formatRunoffDebugCurrency(
                                      step.cumulativeRepaymentAssetTape,
                                    )}
                                  </Td>
                                  <Td isNumeric>
                                    {formatRunoffDebugCurrency(
                                      step.cumulativeRevenueExisting,
                                    )}
                                  </Td>
                                  <Td isNumeric>
                                    {formatRunoffDebugCurrency(step.numerator)}
                                  </Td>
                                  <Td isNumeric>
                                    {formatRunoffDebugCurrency(step.denominator)}
                                  </Td>
                                  <Td isNumeric>
                                    {formatRunoffDebugNumber(step.eval)}
                                  </Td>
                                </Tr>
                              ),
                            )}
                          </Tbody>
                        </Table>
                      </Box>
                    ) : null}
                    {fetchResult?.cashflowRunoffDebugWithoutCash?.steps
                      ?.length > 0 ? (
                      <Box>
                        <Text fontWeight={600} mb={2}>
                          Revenue only (no starting cash in numerator)
                        </Text>
                        <Text fontSize={'xs'} color={'gray.600'} mb={2}>
                          Final month:{' '}
                          {formatRunoffDebugNumber(
                            fetchResult.cashflowRunoffDebugWithoutCash
                              .finalMonthIndex,
                          )}
                          {' · '}
                          Sales at cutoff:{' '}
                          {formatRunoffDebugCurrency(
                            fetchResult.cashflowRunoffDebugWithoutCash
                              .salesAtCutoff,
                          )}
                          {fetchResult.cashflowRunoffDebugWithoutCash
                            .requiresMoreData
                            ? ' *'
                            : ''}
                        </Text>
                        <Table size={'sm'} variant={'simple'} sx={{ minWidth: 'max-content' }}>
                          <Thead>
                            <Tr>
                              <Th>Period</Th>
                              <Th isNumeric>Cum opex</Th>
                              <Th isNumeric>Cum repay (tape)</Th>
                              <Th isNumeric>Cum rev (existing)</Th>
                              <Th isNumeric>Numerator</Th>
                              <Th isNumeric>Denominator</Th>
                              <Th isNumeric>Ratio</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {fetchResult.cashflowRunoffDebugWithoutCash.steps.map(
                              (step, idx) => (
                                <Tr key={`rwo-${String(idx)}`}>
                                  <Td>{step.period}</Td>
                                  <Td isNumeric>
                                    {formatRunoffDebugCurrency(
                                      step.cumulativeOpex,
                                    )}
                                  </Td>
                                  <Td isNumeric>
                                    {formatRunoffDebugCurrency(
                                      step.cumulativeRepaymentAssetTape,
                                    )}
                                  </Td>
                                  <Td isNumeric>
                                    {formatRunoffDebugCurrency(
                                      step.cumulativeRevenueExisting,
                                    )}
                                  </Td>
                                  <Td isNumeric>
                                    {formatRunoffDebugCurrency(step.numerator)}
                                  </Td>
                                  <Td isNumeric>
                                    {formatRunoffDebugCurrency(step.denominator)}
                                  </Td>
                                  <Td isNumeric>
                                    {formatRunoffDebugNumber(step.eval)}
                                  </Td>
                                </Tr>
                              ),
                            )}
                          </Tbody>
                        </Table>
                      </Box>
                    ) : null}
                  </Box>
                  {fetchResult?.cashflowRunwayDebug?.steps?.length > 0 ? (
                    <Box mt={4}>
                      <Text fontWeight={600} mb={2}>
                        Runway
                      </Text>
                      <Text fontSize={'xs'} color={'gray.600'} mb={2}>
                        Starting cash:{' '}
                        {formatRunoffDebugCurrency(
                          fetchResult.cashflowRunwayDebug.startingCash,
                        )}
                        {' · '}
                        Final month:{' '}
                        {formatRunoffDebugNumber(
                          fetchResult.cashflowRunwayDebug.finalMonthIndex,
                        )}
                        {' · '}
                        Cum revenue at cutoff:{' '}
                        {formatRunoffDebugCurrency(
                          fetchResult.cashflowRunwayDebug.salesAtCutoff,
                        )}
                        {fetchResult.cashflowRunwayDebug.requiresMoreData
                          ? ' *'
                          : ''}
                      </Text>
                      <Table size={'sm'} variant={'simple'} sx={{ minWidth: 'max-content' }}>
                        <Thead>
                          <Tr>
                            <Th>Period</Th>
                            <Th isNumeric>Cum opex</Th>
                            <Th isNumeric>Cum revenue</Th>
                            <Th isNumeric>Numerator</Th>
                            <Th isNumeric>Denominator</Th>
                            <Th isNumeric>Ratio</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {fetchResult.cashflowRunwayDebug.steps.map(
                            (step, idx) => (
                              <Tr key={`rwy-${String(idx)}`}>
                                <Td>{step.period}</Td>
                                <Td isNumeric>
                                  {formatRunoffDebugCurrency(step.cumulativeOpex)}
                                </Td>
                                <Td isNumeric>
                                  {formatRunoffDebugCurrency(
                                    step.cumulativeRevenueExisting,
                                  )}
                                </Td>
                                <Td isNumeric>
                                  {formatRunoffDebugCurrency(step.numerator)}
                                </Td>
                                <Td isNumeric>
                                  {formatRunoffDebugCurrency(step.denominator)}
                                </Td>
                                <Td isNumeric>
                                  {formatRunoffDebugNumber(step.eval)}
                                </Td>
                              </Tr>
                            ),
                          )}
                        </Tbody>
                      </Table>
                    </Box>
                  ) : null}

                  {(fetchResult?.cashflowRunoffDebugWithCash?.requiresMoreData ||
                    fetchResult?.cashflowRunoffDebugWithoutCash?.requiresMoreData ||
                    fetchResult?.cashflowRunwayDebug?.requiresMoreData) ? (
                    <Text fontSize={'xs'} color={'gray.500'} mt={2}>
                      * Ratio did not fall below 1.0 within the modeled months;
                      KPI uses the full horizon.
                    </Text>
                  ) : null}
                </Collapse>
              </Box>
            ) : null}

            {fetchResult?.cashflowScenarioId ? (
              <Box mt={4}>
                <Flex gap={3} flexWrap={'wrap'} alignItems={'center'}>
                  <Button
                    size={'sm'}
                    variant={'outline'}
                    onClick={() =>
                      setCashflowDetailTableOpen((open) => !open)
                    }
                  >
                    {cashflowDetailTableOpen
                      ? 'Hide cashflow results table'
                      : 'Show cashflow results table'}
                  </Button>
                  {!cashflowEmbed.loading && cashflowEmbed.results ? (
                    <>
                      <RepaymentsPerCohortCsv
                        modelResults={cashflowEmbed.results}
                      />
                      <CohortBreakdownCsv
                        cohortRevenueBreakdown={
                          cashflowEmbed.results.cohort_revenue_breakdown
                        }
                        modelResults={cashflowEmbed.results.items}
                      />
                    </>
                  ) : null}
                </Flex>
                {cashflowDetailTableOpen ? (
                  <Box mt={4} overflowX={'auto'} maxW={'100%'}>
                    {cashflowEmbed.loading ? (
                      <Flex py={6} justify={'center'}>
                        <Spinner />
                      </Flex>
                    ) : null}
                    {cashflowEmbed.error ? (
                      <Text color={'red.500'} fontSize={'sm'}>
                        Saved cashflow results could not be loaded.
                      </Text>
                    ) : null}
                    {!cashflowEmbed.loading &&
                    cashflowEmbed.results?.items ? (
                      <ResultsTable
                        modelResults={cashflowEmbed.results.items}
                        cohortsRawData={cashflowEmbed.results.cohorts}
                        roasVector={cashflowEmbed.results.roas_100_avg_6}
                        kpiVectors={cashflowEmbed.results.kpis?.vectors}
                      />
                    ) : null}
                    {!cashflowEmbed.loading &&
                    !cashflowEmbed.error &&
                    fetchResult.cashflowScenarioId &&
                    !cashflowEmbed.results?.items ? (
                      <Text fontSize={'sm'} color={'gray.500'}>
                        No saved table for this run yet. Fetch the tearsheet
                        again to rebuild cashflow.
                      </Text>
                    ) : null}
                  </Box>
                ) : null}
              </Box>
            ) : null}
            </Collapse>
          </Card>
        )}
      </Card>

      <Drawer
        isOpen={cashOverrideDrawerDisclosure.isOpen}
        placement={'right'}
        onClose={cashOverrideDrawerDisclosure.onClose}
        size={'sm'}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Edit Field</DrawerHeader>
          <DrawerBody>
            <FormControl mb={4}>
              <FormLabel>Installment Cash Balance</FormLabel>
              <Input
                value={cashBalanceOverrideDraft}
                onChange={(event) =>
                  setCashBalanceOverrideDraft(
                    normalizeNumericInputValue(event.target.value)
                  )
                }
                placeholder={'Enter cash balance'}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Comments</FormLabel>
              <Input
                value={cashBalanceOverrideComment}
                onChange={(event) => setCashBalanceOverrideComment(event.target.value)}
                placeholder={'Explain why you changed this value'}
              />
            </FormControl>
          </DrawerBody>
          <DrawerFooter>
            <Button variant={'brand'} onClick={applyCashOverride}>
              Apply Changes
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      <Drawer
        isOpen={scorecardOverrideDrawerDisclosure.isOpen}
        placement={'right'}
        onClose={() => {
          setScorecardOverrideDrawerViewOnly(false);
          scorecardOverrideDrawerDisclosure.onClose();
        }}
        size={'sm'}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>
            {scorecardOverrideDrawerViewOnly
              ? 'View score override'
              : 'Edit score'}
          </DrawerHeader>
          <DrawerBody>
            <FormControl mb={4}>
              <FormLabel>Score</FormLabel>
              <Select
                value={scorecardOverrideDraftScore}
                onChange={(event) =>
                  setScorecardOverrideDraftScore(event.target.value)
                }
                placeholder={'Select score'}
                isDisabled={scorecardOverrideDrawerViewOnly}
              >
                <option value={'3'}>3</option>
                <option value={'2'}>2</option>
                <option value={'1'}>1</option>
              </Select>
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Reason</FormLabel>
              <Input
                value={scorecardOverrideDraftReason}
                onChange={(event) =>
                  setScorecardOverrideDraftReason(event.target.value)
                }
                placeholder={'Explain why you changed this score'}
                isReadOnly={scorecardOverrideDrawerViewOnly}
              />
            </FormControl>
            <FormControl>
              <FormLabel>New value</FormLabel>
              <Input
                value={scorecardOverrideDraftNumericValue}
                onChange={(event) =>
                  setScorecardOverrideDraftNumericValue(
                    normalizeNumericInputValue(event.target.value, {
                      allowNegative: true,
                    })
                  )
                }
                placeholder={'Numeric value used for this score'}
                isReadOnly={scorecardOverrideDrawerViewOnly}
              />
            </FormControl>
          </DrawerBody>
          <DrawerFooter>
            {!scorecardOverrideDrawerViewOnly ? (
              <Button variant={'brand'} onClick={applyScorecardScoreOverride}>
                Apply Changes
              </Button>
            ) : null}
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      <Drawer
        isOpen={configurationDrawerDisclosure.isOpen}
        placement={'right'}
        onClose={configurationDrawerDisclosure.onClose}
        size={'sm'}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Tearsheet Configuration</DrawerHeader>
          <DrawerBody>
            <Heading size={'sm'} mb={4} color={'gray.700'}>
              Cashflow & repayment
            </Heading>
            <FormControl mb={5}>
              <FormLabel>Marketing Increase MoM (%)</FormLabel>
              <Input
                type={'number'}
                value={draftConfigState.marketingMoMGrowthPct}
                onChange={(event) =>
                  setDraftConfigState({
                    ...draftConfigState,
                    marketingMoMGrowthPct: Number(event.target.value || 0),
                  })
                }
              />
            </FormControl>

            <FormControl mb={5}>
              <FormLabel>XIRR (%)</FormLabel>
              <Input
                type={'number'}
                value={draftConfigState.xirrPct}
                onChange={(event) =>
                  setDraftConfigState({
                    ...draftConfigState,
                    xirrPct: Number(event.target.value || 0),
                  })
                }
              />
            </FormControl>

            <FormControl mb={5}>
              <FormLabel>Number of repayments</FormLabel>
              <Input
                type={'number'}
                value={draftConfigState.numberOfRepayments}
                onChange={(event) =>
                  setDraftConfigState({
                    ...draftConfigState,
                    numberOfRepayments: Number(event.target.value || 0),
                  })
                }
              />
            </FormControl>

            <FormControl mb={5}>
              <FormLabel>Grace period (months)</FormLabel>
              <Input
                type={'number'}
                value={draftConfigState.gracePeriod}
                onChange={(event) =>
                  setDraftConfigState({
                    ...draftConfigState,
                    gracePeriod: Number(event.target.value || 0),
                  })
                }
              />
            </FormControl>

            <Divider borderColor={'gray.200'} my={6} />

            <Heading size={'sm'} mb={2} color={'gray.700'}>
              ROAS Cohort Performance
            </Heading>
            <Text fontSize={'sm'} color={'gray.600'} mb={4}>
              Prediction model and include refunds apply only to the ROAS cohort grid
              (predicted periods and refund-inclusive ROAS measures).
            </Text>

            <FormControl mb={5}>
              <FormLabel>Prediction Model</FormLabel>
              <RadioGroup
                value={draftConfigState.predictionModel}
                onChange={(value) =>
                  setDraftConfigState({
                    ...draftConfigState,
                    predictionModel: value,
                  })
                }
              >
                <HStack spacing={6}>
                  <Radio value={'linear'}>Linear</Radio>
                  <Radio value={'markov'}>Markov</Radio>
                  <Radio value={'combined'}>Combined</Radio>
                </HStack>
              </RadioGroup>
            </FormControl>

            <FormControl mb={0}>
              <FormLabel>Include Refunds</FormLabel>
              <Switch
                isChecked={draftConfigState.includeRefunds}
                onChange={(event) =>
                  setDraftConfigState({
                    ...draftConfigState,
                    includeRefunds: event.target.checked,
                  })
                }
              />
            </FormControl>
          </DrawerBody>
          <DrawerFooter>
            <HStack>
              <Button variant={'outline'} onClick={configurationDrawerDisclosure.onClose}>
                Cancel
              </Button>
              <Button variant={'brand'} onClick={onSaveConfiguration}>
                Save
              </Button>
            </HStack>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      <Modal isOpen={saveModalDisclosure.isOpen} onClose={saveModalDisclosure.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Save Tearsheet</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>Tearsheet Name</FormLabel>
              <Input
                value={saveName}
                onChange={(event) => setSaveName(event.target.value)}
                placeholder={'Enter tearsheet name'}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={saveModalDisclosure.onClose}>
              Cancel
            </Button>
            <Button
              variant={'brand'}
              onClick={onSaveTearsheet}
              isDisabled={!saveName.trim()}
              isLoading={isSaving}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <CockpitAssistFab
        isVisible={!!fetchResult?.resultRows?.length}
        title="AI Summary"
        subtitle="Credit committee summary of this tearsheet"
        onSend={handleTearsheetAssistSend}
        placeholder="Summarize this tearsheet or ask a question…"
        disabled={isFetching}
      />
    </Box>
  );
};

TearsheetNew.propTypes = {};

export default TearsheetNew;
